import React, { useState, useMemo, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { HOME } from "@/constants/testIds";
import LoginPage from "./LoginPage";

import VerifyEmailPage from "./VerifyEmailPage";
import ForgotPasswordPage from "./ForgotPasswordPage";
import ResetPasswordPage from "./ResetPasswordPage";
import TermsOfUsePage from "./TermsOfUsePage";
import DisclaimerPage from "./DisclaimerPage";
import AdminPanel from "./AdminPanel";
import OnboardingTour from "./OnboardingTour";
import PrivacyPage from "./PrivacyPage";
import AgeGate from "./AgeGate";
import { 
  Search, 
  X, 
  Loader2, 
  MessageSquare,
  Share2,
  Mail,
  RotateCw,
  ChevronRight,
  Clock,
  History,
  ArrowRight,
  LogOut,
  Bookmark,
  BookmarkCheck,
  Flag,
  User,
  Settings,
  ExternalLink,
  HelpCircle,
  Bell,
  BellOff
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// ── Cross-browser auth: attach stored JWT as Bearer token on every request ──
// This ensures auth works on Safari, Firefox, and Edge where HttpOnly cookies
// may be blocked by ITP or privacy settings.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("arvay_token");
  if (token) {
    config.headers = config.headers ?? {};
    if (!config.headers["Authorization"]) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return config;
});

// ── Cross-browser clipboard helper ──
// navigator.clipboard requires HTTPS focus-state in Safari; falls back to
// the deprecated execCommand which still works in all major browsers.
async function copyToClipboard(text) {
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try { await navigator.clipboard.writeText(text); return true; } catch {}
  }
  const el = document.createElement("textarea");
  el.value = text;
  el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
  document.body.appendChild(el);
  el.focus();
  el.select();
  try {
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    document.body.removeChild(el);
    return false;
  }
}

const NEWS_DATA = {
  Innovation: [
    {
      id: "i1",
      title: "Sovereign AI Networks and the Architecture of Public Knowledge",
      description: "How localized research laboratories are training models to preserve regional linguistics and digital autonomy.",
      source: "GDELT",
      time: "10m ago",
      readTime: "4 min read"
    },
    {
      id: "i2",
      title: "Quantum Cryptography Deployed in Global Ingress Protocols",
      description: "A silent revolution in secure communications as trans-Pacific fiber lines begin pilot runs of quantum-key distribution.",
      source: "Reuters",
      time: "2h ago",
      readTime: "6 min read"
    },
    {
      id: "i3",
      title: "The Evolution of Decentralized Semantic Search Engines",
      description: "How distributed vector indexers are transitioning away from commercial crawlers to sovereign protocol repositories.",
      source: "The Hindu",
      time: "1d ago",
      readTime: "5 min read"
    },
    {
      id: "i4",
      title: "Neuromorphic Processing Units Advance Automated Threat Assessments",
      description: "Silicon systems mimicking biological neural lattices clear pilot milestones, processing media telemetry 50x more efficiently.",
      source: "Reuters",
      time: "2d ago",
      readTime: "7 min read"
    }
  ],
  Regional: [
    {
      id: "n1",
      title: "Prime Minister Narendra Modi Outlines Strategic Automated Ports Vision",
      description: "Addressing the Maritime Summit, Narendra Modi announced full integration of autonomous cargo handling at strategic shipping corridors to accelerate port clearance times.",
      source: "The Hindu",
      time: "30m ago",
      readTime: "5 min read"
    },
    {
      id: "n2",
      title: "Strategic Semiconductor Reserves Established to Stabilize Domestic Supply",
      description: "The Commerce Department announces critical stockpiles to buffer high-end manufacturing against international logistical shifts.",
      source: "Reuters",
      time: "1h ago",
      readTime: "5 min read"
    },
    {
      id: "n3",
      title: "Economic Briefing: Narendra Modi Finalizes Trade Alliances with Global Partners",
      description: "Sovereign policy panels conclude multilateral reviews following Prime Minister Modi's meetings on digital ledger settlements.",
      source: "Reuters",
      time: "3h ago",
      readTime: "6 min read"
    },
    {
      id: "n4",
      title: "Unified Grid Modernization Bill Receives Broad Legislative Sanction",
      description: "Decade-long infrastructure plans clear legislative bodies, dedicating billions to high-capacity automated power routing.",
      source: "The Hindu",
      time: "4h ago",
      readTime: "4 min read"
    }
  ],
  Global: [
    {
      id: "int1",
      title: "Global Maritime Routes Realigned Amid Sovereign Deep-Water Upgrades",
      description: "A coordinated push by global port authorities modernizes maritime docking systems to preempt supply bottleneck nodes.",
      source: "Reuters",
      time: "30m ago",
      readTime: "6 min read"
    },
    {
      id: "int2",
      title: "Cross-Border Synchronized Digital Currency Settlement Pilot Launches",
      description: "Thirteen monetary authorities initiate multi-institutional testing of digital ledgers to decrease reliance on legacy clears.",
      source: "The Hindu",
      time: "5h ago",
      readTime: "8 min read"
    },
    {
      id: "int3",
      title: "Global Aerospace Leaders Convene for Orbital Transit Talks",
      description: "Multilateral delegates finalize collaborative satellite telemetry tracking standards and high-resolution imaging grids.",
      source: "Reuters",
      time: "1d ago",
      readTime: "6 min read"
    },
    {
      id: "int4",
      title: "Multilateral Accords Formulated for Near-Earth Orbital Cleanliness",
      description: "Twenty-four spacefaring nations sign binding protocols on orbital debris removal and satellite tracking standards.",
      source: "GDELT",
      time: "3d ago",
      readTime: "5 min read"
    }
  ],
  Stocks: [
    {
      id: "stk1",
      title: "Global Markets Rally on Strong Earnings Season",
      description: "Major indices hit new highs as Q4 earnings beat expectations across technology and consumer sectors.",
      source: "MarketWatch",
      time: "1h ago",
      readTime: "3 min read"
    },
    {
      id: "stk2",
      title: "Central Banks Indicate Cautious Rate Trajectory for 2026",
      description: "Federal Reserve and ECB officials hint at gradual easing, with data dependency driving every decision.",
      source: "CNBC Finance",
      time: "3h ago",
      readTime: "4 min read"
    }
  ],
  Horoscope: [
    {
      id: "horo1",
      title: "Mercury Retrograde Ends: Communication Channels Reopen for All Signs",
      description: "The three-week planetary transit concludes, bringing clarity to stalled conversations and delayed projects across the zodiac.",
      source: "AstroStyle",
      time: "6h ago",
      readTime: "3 min read"
    },
    {
      id: "horo2",
      title: "Full Moon in Scorpio: Transformation and Release for Earth Signs",
      description: "This week's lunation brings deep emotional insights for Taurus, Virgo, and Capricorn as hidden truths surface.",
      source: "Cafe Astrology",
      time: "1d ago",
      readTime: "4 min read"
    }
  ]
};

// Format an ISO timestamp into a compact relative label (e.g. "10m ago", "2h ago").
const formatRelativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

// All integrated RSS sources — publication name + favicon domain only (no URLs shown to users)
const ALL_SOURCES = [
  // Innovation
  { name: "The Verge",             domain: "theverge.com",            category: "Innovation" },
  { name: "Ars Technica",          domain: "arstechnica.com",         category: "Innovation" },
  { name: "MIT Technology Review", domain: "technologyreview.com",    category: "Innovation" },
  { name: "The Guardian Tech",    domain: "theguardian.com",         category: "Innovation" },
  { name: "NY Times Tech",        domain: "nytimes.com",             category: "Innovation" },
  { name: "Axios",                 domain: "axios.com",               category: "Innovation" },
  { name: "CNBC Tech",             domain: "cnbc.com",                category: "Innovation" },
  { name: "TechCrunch",            domain: "techcrunch.com",          category: "Innovation" },
  { name: "Wired",                 domain: "wired.com",               category: "Innovation" },
  { name: "VentureBeat",           domain: "venturebeat.com",         category: "Innovation" },
  { name: "IEEE Spectrum",         domain: "spectrum.ieee.org",       category: "Innovation" },
  { name: "Engadget",              domain: "engadget.com",            category: "Innovation" },
  { name: "Forbes India",          domain: "forbesindia.com",          category: "Innovation" },
  { name: "Inc42",                 domain: "inc42.com",               category: "Innovation" },
  // Global
  { name: "BBC",                   domain: "bbc.com",                 category: "Global" },
  { name: "Al Jazeera",            domain: "aljazeera.com",           category: "Global" },
  { name: "The Guardian",          domain: "theguardian.com",         category: "Global" },
  { name: "NY Times",              domain: "nytimes.com",             category: "Global" },
  { name: "NPR",                   domain: "npr.org",                 category: "Global" },
  { name: "DW Deutsche Welle",     domain: "dw.com",                  category: "Global" },
  { name: "RFI",                   domain: "rfi.fr",                  category: "Global" },
  { name: "NHK World Japan",       domain: "nhk.or.jp",               category: "Global" },
  { name: "Channel NewsAsia",      domain: "channelnewsasia.com",     category: "Global" },
  { name: "ABC Australia",         domain: "abc.net.au",              category: "Global" },
  { name: "Arab News",             domain: "arabnews.com",            category: "Global" },
  { name: "ProPublica",            domain: "propublica.org",          category: "Global" },
  { name: "BBC World Service",     domain: "bbc.co.uk",               category: "Global" },
  { name: "TIME",                  domain: "time.com",                category: "Global" },
  { name: "The Atlantic",          domain: "theatlantic.com",         category: "Global" },
  { name: "Bloomberg",             domain: "bloomberg.com",           category: "Global" },
  { name: "The Economist",         domain: "economist.com",           category: "Global" },
  { name: "South China Morning Post", domain: "scmp.com",            category: "Global" },
  { name: "Nikkei Asia",           domain: "asia.nikkei.com",         category: "Global" },
  // Regional
  { name: "The Hindu",             domain: "thehindu.com",            category: "Regional" },
  { name: "NDTV",                  domain: "ndtv.com",                category: "Regional" },
  { name: "Times of India",        domain: "timesofindia.indiatimes.com", category: "Regional" },
  { name: "Economic Times",        domain: "economictimes.indiatimes.com", category: "Regional" },
  { name: "Livemint",              domain: "livemint.com",            category: "Regional" },
  { name: "Hindustan Times",       domain: "hindustantimes.com",      category: "Regional" },
  { name: "Indian Express",        domain: "indianexpress.com",       category: "Regional" },
  { name: "The Wire",              domain: "thewire.in",              category: "Regional" },
  { name: "Dainik Bhaskar",        domain: "bhaskar.com",             category: "Regional" },
  { name: "Financial Express",     domain: "financialexpress.com",    category: "Regional" },
  { name: "ESPN Cricinfo",         domain: "espncricinfo.com",        category: "Regional" },
  // Shares/Stocks
  { name: "MarketWatch",           domain: "marketwatch.com",         category: "Shares/Stocks" },
  { name: "CNBC Finance",          domain: "cnbc.com",                category: "Shares/Stocks" },
  { name: "The Guardian Finance",  domain: "theguardian.com",         category: "Shares/Stocks" },
  { name: "NY Times Business",     domain: "nytimes.com",             category: "Shares/Stocks" },
  { name: "Investing.com",         domain: "investing.com",           category: "Shares/Stocks" },
  { name: "Reuters Business",      domain: "reuters.com",             category: "Shares/Stocks" },
  { name: "Financial Times",       domain: "ft.com",                  category: "Shares/Stocks" },
  { name: "Financial Express",     domain: "financialexpress.com",    category: "Shares/Stocks" },
  { name: "Moneycontrol",          domain: "moneycontrol.com",        category: "Shares/Stocks" },
  { name: "Business Standard",     domain: "business-standard.com",   category: "Shares/Stocks" },
  { name: "The Hindu BusinessLine", domain: "thehindubusinessline.com", category: "Shares/Stocks" },
  // Horoscope
  { name: "AstroStyle",            domain: "astrostyle.com",          category: "Horoscope" },
  { name: "Cafe Astrology",        domain: "cafeastrology.com",       category: "Horoscope" },
  { name: "AstroSage",             domain: "astrosage.com",           category: "Horoscope" },
  { name: "FindYourFate",          domain: "findyourfate.com",        category: "Horoscope" },
  // Community — Reddit
  { name: "r/worldnews",           domain: "reddit.com",              category: "Community" },
  { name: "r/business",            domain: "reddit.com",              category: "Community" },
  { name: "r/stocks",              domain: "reddit.com",              category: "Community" },
  { name: "r/india",               domain: "reddit.com",              category: "Community" },
  { name: "r/technology",          domain: "reddit.com",              category: "Community" },
  // Community — YouTube
  { name: "BBC News YT",           domain: "youtube.com",             category: "Community" },
  { name: "Al Jazeera YT",         domain: "youtube.com",             category: "Community" },
  { name: "DW News YT",            domain: "youtube.com",             category: "Community" },
  { name: "NDTV YT",               domain: "youtube.com",             category: "Community" },
];

const CATEGORY_ORDER = ["Innovation", "Global", "Regional", "Shares/Stocks", "Horoscope", "Community"];

const PREMIUM_DOMAINS = new Set(["bloomberg.com", "ft.com", "wsj.com"]);

const SourcesPanel = () => {
  const [open, setOpen] = React.useState(false);
  const grouped = CATEGORY_ORDER.map(cat => ({
    cat,
    sources: ALL_SOURCES.filter(s => s.category === cat),
  }));

  return (
    <div className="mt-10 mb-6 border border-[#E0E0E0] bg-white" data-testid="sources-panel">
      {/* Accordion toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="sources-panel-toggle"
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-[#FAFAFA] transition-colors focus:outline-none group"
      >
        <div className="flex items-center gap-3">
          <span className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A1A1A]">
            Integrated Sources
          </span>
          <span className="font-sans text-[10px] text-neutral-400 border border-[#E0E0E0] px-2 py-0.5">
            {ALL_SOURCES.length} publications
          </span>
        </div>
        <ChevronRight
          className={`w-3.5 h-3.5 text-neutral-400 transition-transform duration-300 ${open ? "rotate-90" : ""}`}
        />
      </button>

      {/* Expanded content */}
      {open && (
        <div className="px-6 pb-6 border-t border-[#E0E0E0] animate-fade-in">
          <div className="mt-5 space-y-6">
            {grouped.map(({ cat, sources }) => (
              <div key={cat}>
                <p className="font-sans text-[9px] uppercase tracking-[0.25em] font-bold text-neutral-400 mb-3">
                  {cat}
                </p>
                <div className="flex flex-wrap gap-3">
                  {sources.map(({ name, domain }) => (
                    <div
                      key={`${cat}-${domain}-${name}`}
                      className="flex flex-col gap-1"
                    >
                      <div
                        data-testid={`source-${name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-center gap-2 border border-[#E0E0E0] hover:border-black px-3 py-2 transition-colors bg-white group"
                      >
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
                          alt=""
                          width={14}
                          height={14}
                          className="w-3.5 h-3.5 object-contain opacity-75 group-hover:opacity-100 transition-opacity"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <span className="font-sans text-xs text-[#1A1A1A] whitespace-nowrap">
                          {name}
                        </span>
                      </div>
                      {PREMIUM_DOMAINS.has(domain) && (
                        <span className="font-sans text-[9px] text-neutral-400 italic leading-tight px-0.5">
                          Short preview only. Full article at original source.
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Keyword highlight engine ─────────────────────────────────────────────
// Case-insensitive, wraps every token from `query` found in `text` with <mark>.
// Splitting by a capturing-group regex means odd-index parts are the matched tokens.
const HighlightText = ({ text, query, className, ...rest }) => {
  if (!query?.trim() || !text) return <span className={className} {...rest}>{text}</span>;
  const keywords = query.trim().split(/\s+/).filter(k => k.length > 1);
  if (!keywords.length) return <span className={className} {...rest}>{text}</span>;
  const escaped = keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  return (
    <span className={className} {...rest}>
      {parts.map((part, i) =>
        i % 2 === 1
          ? <mark key={i} className="bg-yellow-100/60 font-semibold rounded-sm not-italic">{part}</mark>
          : part
      )}
    </span>
  );
};

const DashboardPage = ({ onLogout, authUser, onTourStart }) => {
  // Active category tab
  const tabs = ["My Topics", "Innovation", "Global", "Regional", "Shares/Stocks", "Horoscope", "Community"];
  const [activeTab, setActiveTab] = useState("Innovation");
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Onboarding tour state is owned by App and passed down — see handleAgeAccept for init

  // GDELT Live search state
  const [apiSearchResults, setApiSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  // Publication strip — selected sources filter
  const [selectedSources, setSelectedSources] = useState([]);

  // Bookmarks page visibility
  const [showBookmarksPage, setShowBookmarksPage] = useState(false);
  const [fullBookmarks, setFullBookmarks] = useState([]);

  // Market data state (Yahoo Finance + CoinGecko)
  const [marketIndices, setMarketIndices]     = useState([]);
  const [marketMovers, setMarketMovers]       = useState({ gainers: [], decliners: [] });
  const [cryptoCoins, setCryptoCoins]         = useState([]);
  const [marketLoading, setMarketLoading]     = useState(false);
  const [moversLoading, setMoversLoading]     = useState(false);
  const [cryptoLoading, setCryptoLoading]     = useState(false);
  const [cryptoError, setCryptoError]         = useState(false);

  // Live RSS feed state (per-category cache so readers stay in place, no auto-refresh)
  const [feedData, setFeedData] = useState({});
  const [loadingFeed, setLoadingFeed] = useState(false);

  // Recent searches state (persisted via localStorage)
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const saved = localStorage.getItem("arvay_recent_searches");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // ── Web Push subscription state ──
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Decode the VAPID public key for the Push API
  function _urlB64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  }

  // Check on mount whether there is already an active push subscription
  useEffect(() => {
    if (!authUser || !("serviceWorker" in navigator) || !("PushManager" in window)) return;
    // Use navigator.serviceWorker.ready so we only check registrations that have
    // an active service worker (avoids false positives from installing SWs).
    navigator.serviceWorker.getRegistrations().then(async (regs) => {
      for (const r of regs) {
        if (r.active?.scriptURL?.includes("push-sw.js")) {
          const sub = await r.pushManager.getSubscription();
          setPushEnabled(!!sub);
          return;
        }
      }
      setPushEnabled(false);
    }).catch(() => {});
  }, [authUser]);

  const handleTogglePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      toast.error("Push notifications are not supported in this browser.");
      return;
    }
    setPushLoading(true);
    try {
      if (pushEnabled) {
        // ── Unsubscribe ──
        const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
        const sub = await reg?.pushManager.getSubscription();
        if (sub) {
          await axios.delete(`${API}/push/unsubscribe`, {
            data: { endpoint: sub.endpoint },
            withCredentials: true,
          });
          await sub.unsubscribe();
        }
        setPushEnabled(false);
        toast.success("Notifications disabled.");
      } else {
        // ── Subscribe ──
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          toast.error("Notification permission denied.");
          return;
        }
        // Register the service worker, then use navigator.serviceWorker.ready
        // which guarantees an ACTIVE registration (avoids race where reg.pushManager
        // throws because the SW is still installing).
        await navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
        const activeReg = await navigator.serviceWorker.ready;

        const { data: { publicKey } } = await axios.get(`${API}/push/vapid-public-key`);
        if (!publicKey) throw new Error("VAPID public key unavailable.");

        // Always discard any stale subscription and create a fresh one so that
        // encryption keys (p256dh / auth) are guaranteed to be present.
        const existing = await activeReg.pushManager.getSubscription();
        if (existing) await existing.unsubscribe();

        const sub = await activeReg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: _urlB64ToUint8Array(publicKey),
        });

        const { keys } = sub.toJSON();
        if (!keys?.p256dh || !keys?.auth) {
          throw new Error("Browser subscription missing encryption keys.");
        }

        await axios.post(
          `${API}/push/subscribe`,
          { endpoint: sub.endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } },
          { withCredentials: true }
        );
        setPushEnabled(true);
        toast.success("Notifications enabled — you'll be alerted when your topics make headlines.");
      }
    } catch (err) {
      console.error("[Arvay Push] subscribe error:", err?.name, err?.message, err);
      toast.error(err?.response?.data?.detail || err?.message || "Could not update notification settings.");
    } finally {
      setPushLoading(false);
    }
  };

  // Persistent Device ID with 30-day session — no automatic logout
  const [deviceId] = useState(() => {
    try {
      const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
      const stored = JSON.parse(localStorage.getItem("arvay_session") || "null");
      if (stored && stored.id && stored.createdAt && (Date.now() - stored.createdAt) < THIRTY_DAYS_MS) {
        return stored.id;
      }
      // Migrate legacy device_id if present
      const legacyId = localStorage.getItem("arvay_device_id");
      const id = legacyId || `dev-${Math.random().toString(36).substring(2, 11)}`;
      localStorage.setItem("arvay_session", JSON.stringify({ id, createdAt: Date.now() }));
      return id;
    } catch {
      return "dev-anon";
    }
  });

  // Traffic Bridge: Helper to log UserActivity to the backend which mirrors to FEEDBACK_WEBHOOK_URL
  const logUserActivity = async (action, details = "", extraParams = {}) => {
    try {
      await axios.post(`${API}/activity/log`, { 
        action, 
        details,
        device_id: deviceId,
        tab: activeTab,
        ...extraParams
      });
    } catch (e) {
      console.error("Traffic Bridge logging failed:", e);
    }
  };

  // Google Analytics Event Trigger
  const trackGAEvent = (eventName, params = {}) => {
    if (window.gtag) {
      window.gtag("event", eventName, params);
    }
  };

  const hasLoggedMount = useRef(false);

  // Log page load activity on mount
  useEffect(() => {
    if (!hasLoggedMount.current) {
      hasLoggedMount.current = true;
      logUserActivity("page_load", window.location.href);
      trackGAEvent("page_view", {
        page_title: "Arvay | Read between the digital lines",
        page_location: window.location.href,
        page_path: window.location.pathname
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Early access newsletter state
  const [email, setEmail] = useState("");
  const [submittingEmail, setSubmittingEmail] = useState(false);

  // Feedback modal state
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Refresh feed simulation
  const [refreshing, setRefreshing] = useState(false);

  // Load More pagination
  const [displayCount, setDisplayCount] = useState(10);

  // Regional country selector
  const COUNTRY_LIST = ["India","USA","Switzerland","UK","Germany","France","Australia","Singapore","Japan","UAE","Canada","Netherlands","Sweden","Norway","Denmark","Belgium","Austria","Brazil","South Africa","Israel"];
  const [selectedCountry, setSelectedCountry] = useState("India");

  // Horoscope zodiac filter
  const ZODIAC_SIGNS = ["Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra","Scorpio","Sagittarius","Capricorn","Aquarius","Pisces"];
  const [selectedZodiac, setSelectedZodiac] = useState(null);
  // Daily horoscope reading
  const [dailyHoroscope, setDailyHoroscope] = useState(null);
  const [horoscopeLoading, setHoroscopeLoading] = useState(false);

  // Stocks keyword search
  const [stockQuery, setStockQuery] = useState("");
  // Community sub-tab: "reddit" or "youtube" or "general"
  const [communitySubTab, setCommunitySubTab] = useState("reddit");
  const [redditUnavailable, setRedditUnavailable] = useState(false);
  // My Topics
  const [userTopics, setUserTopics]       = useState([]);
  const [topicsFeed, setTopicsFeed]       = useState([]);
  const [topicInput, setTopicInput]       = useState("");
  const [topicsLoading, setTopicsLoading] = useState(false);
  // Push notification tab settings
  const [showPushSettings, setShowPushSettings] = useState(false);
  const [pushTabSettings, setPushTabSettings]   = useState({
    "My Topics": true, "Innovation": false, "Global": false,
    "Regional": false, "Shares/Stocks": false, "Horoscope": false, "Community": false,
    quiet_hours_enabled: false, quiet_hours_start: "23:00", quiet_hours_end: "07:00",
    min_gap_hours: 1,
  });
  const [pushKwStats, setPushKwStats] = useState({});
  // Bookmarks
  const [bookmarks, setBookmarks]         = useState([]); // array of {url, ...}
  // Profile panel
  const [showProfile, setShowProfile]     = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  // Suggest source modal
  const [showSuggest, setShowSuggest]     = useState(false);
  const [suggestName, setSuggestName]     = useState("");
  const [suggestUrl, setSuggestUrl]       = useState("");
  // Report modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget]       = useState(null);
  const [reportReason, setReportReason]       = useState("");
  // Admin panel
  const [showAdmin, setShowAdmin]         = useState(false);
  // Loading message rotation
  const LOAD_MESSAGES = [
    "Scanning global sources...",
    "Fetching latest headlines...",
    "Curating results for you...",
    "Almost there...",
  ];
  const [loadMsgIdx, setLoadMsgIdx] = useState(0);


  // Back-button / browser-close exit guard
  const [showExitDialog, setShowExitDialog] = useState(false);

  const dropdownRef = useRef(null);

  // Get dynamic today's Day and Date
  const formattedDate = useMemo(() => {
    const today = new Date();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return today.toLocaleDateString('en-US', options);
  }, []);

  // Fetch GDELT DOC API search results with simple debounce to avoid hammer-hitting API
  useEffect(() => {
    const fetchSearchResults = async () => {
      const query = searchQuery.trim();
      if (!query) {
        setApiSearchResults([]);
        return;
      }
      
      setLoadingSearch(true);
      logUserActivity("search_query", query, { keyword: query });
      trackGAEvent("search", {
        search_term: query
      });
      try {
        const response = await axios.get(`${API}/news/search?query=${encodeURIComponent(query)}&tab=${encodeURIComponent(activeTab)}`);
        if (response.data && response.data.articles && response.data.articles.length > 0) {
          setApiSearchResults(response.data.articles);
        } else {
          setApiSearchResults([]);
        }
      } catch (err) {
        console.error("GDELT search query failed or rate-limited, falling back to local dataset:", err);
        setApiSearchResults([]);
      } finally {
        setLoadingSearch(false);
      }
    };

    const timer = setTimeout(() => {
      fetchSearchResults();
    }, 450);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Rotate loading messages every 2s while search is in progress
  useEffect(() => {
    if (!loadingSearch) return;
    const interval = setInterval(() => setLoadMsgIdx(i => (i + 1) % LOAD_MESSAGES.length), 2000);
    return () => clearInterval(interval);
  }, [loadingSearch]);

  // Load user's topics + bookmarks + push settings on mount
  useEffect(() => {
    const loadTopicsAndBookmarks = async () => {
      try {
        const [t, b] = await Promise.all([
          axios.get(`${API}/topics`, { withCredentials: true }),
          axios.get(`${API}/bookmarks`, { withCredentials: true }),
        ]);
        setUserTopics(t.data.keywords || []);
        setBookmarks((b.data.bookmarks || []).map(bm => bm.url));
      } catch {}
      // Load push settings separately (non-blocking)
      try {
        const ps = await axios.get(`${API}/push/settings`, { withCredentials: true });
        if (ps.data?.settings) setPushTabSettings(ps.data.settings);
      } catch {}
    };
    loadTopicsAndBookmarks();
  }, []);

  // Load keyword alert stats whenever the push settings panel opens
  useEffect(() => {
    if (!showPushSettings || !authUser) return;
    axios.get(`${API}/push/keyword-stats`, { withCredentials: true })
      .then(r => { if (r.data?.counts) setPushKwStats(r.data.counts); })
      .catch(() => {});
  }, [showPushSettings, authUser]);
  useEffect(() => {
    // For Regional tab, key the feed cache by tab+country so country changes refetch
    // For Community tab, key by tab+subTab so sub-tab changes refetch
    // "Community:general" is handled by its own useEffect below
    if (activeTab === "Community" && communitySubTab === "general") return;
    const feedKey = activeTab === "Regional" ? `Regional:${selectedCountry}`
                  : activeTab === "Community" ? `Community:${communitySubTab}`
                  : activeTab;
    if (feedData[feedKey]) return;
    let cancelled = false;
    const loadFeed = async () => {
      setLoadingFeed(true);
      try {
        let url = `${API}/news/feed?category=${encodeURIComponent(activeTab)}`;
        if (activeTab === "Regional") url += `&country=${encodeURIComponent(selectedCountry)}`;
        if (activeTab === "Community") url += `&sub_type=${encodeURIComponent(communitySubTab)}`;
        const res = await axios.get(url);
        if (!cancelled && res.data && Array.isArray(res.data.articles)) {
          setFeedData((prev) => ({ ...prev, [feedKey]: res.data.articles }));
        }
      } catch (err) {
        console.error("Live feed fetch failed, using local dataset:", err);
        if (!cancelled) setFeedData((prev) => ({ ...prev, [feedKey]: [] }));
      } finally {
        if (!cancelled) setLoadingFeed(false);
      }
    };
    loadFeed();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedCountry, communitySubTab]);

  // General Reddit: search across all subreddits by active keyword
  useEffect(() => {
    if (activeTab !== "Community" || communitySubTab !== "general") return;
    if (!searchQuery.trim()) {
      setFeedData((prev) => ({ ...prev, "Community:general": [] }));
      return;
    }
    let cancelled = false;
    const loadGeneralReddit = async () => {
      setLoadingFeed(true);
      setRedditUnavailable(false);
      try {
        const res = await axios.get(`${API}/community/reddit/search?keyword=${encodeURIComponent(searchQuery.trim())}`);
        const arts = res.data.articles || [];
        if (!cancelled) {
          setFeedData((prev) => ({ ...prev, "Community:general": arts }));
          if (arts.length === 0) setRedditUnavailable(true);
        }
      } catch {
        if (!cancelled) {
          setFeedData((prev) => ({ ...prev, "Community:general": [] }));
          setRedditUnavailable(true);
        }
      } finally {
        if (!cancelled) setLoadingFeed(false);
      }
    };
    loadGeneralReddit();
    return () => { cancelled = true; };
  }, [activeTab, communitySubTab, searchQuery]);

  // My Topics — load feed when tab selected
  useEffect(() => {
    if (activeTab !== "My Topics") return;
    const loadTopicsFeed = async () => {
      setTopicsLoading(true);
      try {
        const res = await axios.get(`${API}/topics/feed`, { withCredentials: true });
        setTopicsFeed(res.data.articles || []);
      } catch {} finally { setTopicsLoading(false); }
    };
    loadTopicsFeed();
  }, [activeTab, userTopics.length]);

  // Horoscope: fetch daily reading when zodiac sign is selected
  useEffect(() => {
    if (activeTab !== "Horoscope" || !selectedZodiac) {
      setDailyHoroscope(null);
      return;
    }
    const fetchHoroscope = async () => {
      setHoroscopeLoading(true);
      try {
        const res = await axios.get(`${API}/horoscope/daily/${selectedZodiac}`);
        setDailyHoroscope(res.data);
      } catch {
        setDailyHoroscope(null);
      } finally {
        setHoroscopeLoading(false);
      }
    };
    fetchHoroscope();
  }, [selectedZodiac, activeTab]);

  // Market data: fetch when Shares/Stocks tab opens
  useEffect(() => {
    if (activeTab !== "Shares/Stocks") return;
    const fetchMarket = async () => {
      setMarketLoading(true);
      try {
        const res = await axios.get(`${API}/market/indices`);
        setMarketIndices(res.data.indices || []);
      } catch { setMarketIndices([]); }
      finally { setMarketLoading(false); }
    };
    const fetchMovers = async () => {
      setMoversLoading(true);
      try {
        const res = await axios.get(`${API}/market/movers`);
        setMarketMovers(res.data);
      } catch { setMarketMovers({ gainers: [], decliners: [] }); }
      finally { setMoversLoading(false); }
    };
    const fetchCrypto = async () => {
      setCryptoLoading(true); setCryptoError(false);
      try {
        const res = await axios.get(`${API}/crypto/top`);
        setCryptoCoins(res.data.coins || []);
      } catch { setCryptoError(true); setCryptoCoins([]); }
      finally { setCryptoLoading(false); }
    };
    fetchMarket(); fetchMovers(); fetchCrypto();
  }, [activeTab]);

  // Reset source filter when tab changes
  useEffect(() => { setSelectedSources([]); }, [activeTab]);

  // Handle clicking outside recent searches dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Back-button & tab-close guard: push a history entry so we can intercept popstate
  useEffect(() => {
    window.history.pushState({ arvay: true }, "");
    const handlePopState = () => {
      setShowExitDialog(true);
      // Re-push to prevent the actual navigation from completing
      window.history.pushState({ arvay: true }, "");
    };
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Reset pagination whenever the tab or search query changes
  useEffect(() => {
    setDisplayCount(10);
  }, [activeTab, searchQuery]);

  // Save recent searches to localStorage
  const saveSearches = (searches) => {
    setRecentSearches(searches);
    try {
      localStorage.setItem("arvay_recent_searches", JSON.stringify(searches));
    } catch (e) {
      console.error("Failed to save search history:", e);
    }
  };

  // Add search query to recent searches
  const commitSearch = (queryStr) => {
    const cleanQuery = queryStr.trim();
    if (!cleanQuery) return;
    
    logUserActivity("search_commit", cleanQuery, { keyword: cleanQuery });
    const updated = [
      cleanQuery,
      ...recentSearches.filter(q => q.toLowerCase() !== cleanQuery.toLowerCase())
    ].slice(0, 5); // Max 5 items
    
    saveSearches(updated);
  };

  // Resolve the list of articles for a tab: prefer live RSS feed, fall back to curated local set.
  const getTabArticles = (tab) => {
    if (tab === "Community") {
      if (communitySubTab === "general") return feedData["Community:general"] || [];
      const key = `Community:${communitySubTab}`;
      return feedData[key] || [];
    }
    if (tab === "My Topics") return topicsFeed;
    // For Regional tab, use country-keyed cache
    const key = tab === "Regional" ? `Regional:${selectedCountry}` : tab;
    const live = feedData[key];
    return live && live.length > 0 ? live : (NEWS_DATA[tab] || []);
  };

  // Advanced search query filter matching exact phrase or keywords separately
  const filteredNews = useMemo(() => {
    // Normalise chip names to UPPERCASE for case-insensitive match with backend source values
    const selectedUpper = selectedSources.map(s => s.toUpperCase());
    const srcFilter = (n) => selectedUpper.includes((n.source || "").toUpperCase());

    // Tab-specific persistent keyword filter (OR logic, all tabs except My Topics)
    const tabKws = activeTab !== "My Topics"
      ? userTopics.filter(t => (t.tab || "My Topics") === activeTab).map(t => t.keyword.toLowerCase())
      : [];
    const tabKwFilter = (n) => {
      if (tabKws.length === 0) return true;
      const text = `${(n.title || "").toLowerCase()} ${(n.description || "").toLowerCase()}`;
      return tabKws.some(kw => text.includes(kw));
    };

    // ── COMMUNITY TAB: apply AND-logic keyword filter + source filter ────────
    if (activeTab === "Community") {
      let base = getTabArticles(activeTab);
      // General Reddit sub-tab: backend already searched by keyword — skip client-side filters
      if (communitySubTab === "general") return base;
      // Apply keyword filter (primary query, AND logic)
      if (searchQuery.trim()) {
        const kw = searchQuery.trim().toLowerCase();
        base = base.filter((n) => {
          const text = `${(n.title || "").toLowerCase()} ${(n.description || "").toLowerCase()}`;
          return text.includes(kw);
        });
      }
      if (selectedSources.length > 0) base = base.filter(srcFilter);
      return base.filter(tabKwFilter);
    }

    // ── MY TOPICS: source filter only (tab keyword feed, unchanged) ──────────
    if (activeTab === "My Topics") {
      const base = getTabArticles(activeTab);
      return selectedSources.length > 0 ? base.filter(srcFilter) : base;
    }

    // ── SHARES/STOCKS + HOROSCOPE: source filter + tab keyword filter ────────
    if (activeTab === "Shares/Stocks" || activeTab === "Horoscope") {
      let base = getTabArticles(activeTab);
      if (selectedSources.length > 0) base = base.filter(srcFilter);
      return base.filter(tabKwFilter);
    }

    // ── INNOVATION / GLOBAL / REGIONAL ──────────────────────────────────────
    // 1. GDELT/RSS backend search results (already keyword-filtered by server)
    if (searchQuery.trim() && apiSearchResults.length > 0) {
      let base = selectedSources.length > 0 ? apiSearchResults.filter(srcFilter) : apiSearchResults;
      return base.filter(tabKwFilter);
    }

    // 2. No active query → show this tab's live feed, with source + tab keyword filter
    if (!searchQuery.trim()) {
      let base = getTabArticles(activeTab);
      if (selectedSources.length > 0) base = base.filter(srcFilter);
      return base.filter(tabKwFilter);
    }

    // 3. Backend returned nothing → local keyword fallback.
    //    Strict: only pull from THIS tab's articles (no cross-tab bleed).
    const allNews = getTabArticles(activeTab);
    const seen = new Set();
    const uniqueNews = allNews.filter((n) => {
      if (seen.has(n.id)) return false;
      seen.add(n.id);
      return true;
    });

    const query = searchQuery.trim().toLowerCase();
    const quoteRegex = /"([^"]+)"/g;
    let exactPhrases = [];
    let match;
    while ((match = quoteRegex.exec(query)) !== null) {
      exactPhrases.push(match[1]);
    }
    const queryWithoutPhrases = query.replace(quoteRegex, "");
    const keywords = queryWithoutPhrases.split(/\s+/).filter((word) => word.length > 0);

    const keywordFiltered = uniqueNews.filter((news) => {
      const title = (news.title || "").toLowerCase();
      const description = (news.description || "").toLowerCase();
      const combinedText = `${title} ${description}`;
      for (const phrase of exactPhrases) {
        if (!combinedText.includes(phrase)) return false;
      }
      for (const word of keywords) {
        if (!combinedText.includes(word)) return false;
      }
      return true;
    });

    let result = selectedSources.length > 0 ? keywordFiltered.filter(srcFilter) : keywordFiltered;
    return result.filter(tabKwFilter);
  }, [activeTab, searchQuery, apiSearchResults, feedData, selectedCountry, communitySubTab, topicsFeed, selectedSources, userTopics]);

  // Topic keyword helpers (works for all tabs)
  const addTopic = async (kw) => {
    if (!kw.trim()) return;
    try {
      const res = await axios.post(`${API}/topics`, { keyword: kw.trim(), tab: activeTab }, { withCredentials: true });
      setUserTopics(prev => [...prev, res.data]);
      setTopicInput("");
      toast.success(`"${kw.trim()}" added to ${activeTab}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add keyword"); }
  };
  const removeTopic = async (id, kw) => {
    await axios.delete(`${API}/topics/${id}`, { withCredentials: true });
    setUserTopics(prev => prev.filter(t => t.id !== id));
    toast("Keyword removed");
  };

  // Bookmark helpers
  const isBookmarked = (url) => bookmarks.includes(url);
  const toggleBookmark = async (article) => {
    if (isBookmarked(article.url)) {
      await axios.delete(`${API}/bookmarks`, { data: { url: article.url }, withCredentials: true });
      setBookmarks(prev => prev.filter(u => u !== article.url));
      toast("Bookmark removed");
    } else {
      await axios.post(`${API}/bookmarks`, {
        url: article.url, title: article.title, source: article.source,
        image: article.image, description: article.description, category: article.category,
      }, { withCredentials: true });
      setBookmarks(prev => [...prev, article.url]);
      toast.success("Article saved");
    }
  };

  // Share helper (Web Share API with text format)
  const shareArticle = async (article) => {
    const shareText = `${article.title} — ${article.url}\nArvay - Read between the digital lines.`;
    if (navigator.share) {
      try { await navigator.share({ title: article.title, text: shareText }); return; } catch {}
    }
    const ok = await copyToClipboard(shareText);
    if (ok) toast.success("Link copied to clipboard");
  };

  // Report content
  const openReportModal = (article) => {
    setReportTarget(article);
    setReportReason("");
    setShowReportModal(true);
  };

  const submitReport = async () => {
    if (!reportTarget || !reportReason) return;
    try {
      await axios.post(`${API}/content/report`, { url: reportTarget.url, title: reportTarget.title, reason: reportReason }, { withCredentials: true });
      toast.success("Reported — thank you");
      setShowReportModal(false); setReportTarget(null); setReportReason("");
    } catch { toast.error("Could not submit report"); }
  };

  // Submit source suggestion
  const submitSuggestion = async () => {
    if (!suggestName.trim() || !suggestUrl.trim()) { toast.error("Both fields are required"); return; }
    try {
      await axios.post(`${API}/sources/suggest`, { name: suggestName.trim(), url: suggestUrl.trim() }, { withCredentials: true });
      toast.success("Suggestion submitted — thank you!");
      setShowSuggest(false); setSuggestName(""); setSuggestUrl("");
    } catch { toast.error("Could not submit suggestion"); }
  };

  // Update display name
  const updateDisplayName = async () => {
    if (!newDisplayName.trim()) return;
    try {
      await axios.put(`${API}/auth/profile`, { name: newDisplayName.trim() }, { withCredentials: true });
      toast.success("Display name updated");
      setShowProfile(false); setNewDisplayName("");
    } catch { toast.error("Update failed"); }
  };
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      let url = `${API}/news/feed?category=${encodeURIComponent(activeTab)}`;
      if (activeTab === "Regional") url += `&country=${encodeURIComponent(selectedCountry)}`;
      if (activeTab === "Community") url += `&sub_type=${encodeURIComponent(communitySubTab)}`;
      const res = await axios.get(url);
      if (res.data && Array.isArray(res.data.articles)) {
        const feedKey = activeTab === "Regional" ? `Regional:${selectedCountry}`
                      : activeTab === "Community" ? `Community:${communitySubTab}`
                      : activeTab;
        setFeedData((prev) => ({ ...prev, [feedKey]: res.data.articles }));
      }
      toast.success("Feed refreshed successfully");
    } catch {
      toast.error("Could not refresh the feed right now.");
    } finally {
      setRefreshing(false);
    }
  };

  // Global Viral Share logic
  const handleShare = async () => {
    try {
      const sharedArticle = filteredNews[0] || { title: "Arvay Intelligence Terminal", id: "ref" };
      const articleLink = (sharedArticle.url && sharedArticle.url !== "#")
        ? sharedArticle.url
        : `${window.location.origin}${window.location.pathname}?ref=${sharedArticle.id}`;
      const shareContent = `${sharedArticle.title} — ${articleLink}\nArvay - Read between the digital lines`;
      
      logUserActivity("global_share", sharedArticle.title, { source: sharedArticle.source || "", link: articleLink });
      const ok = await copyToClipboard(shareContent);
      if (ok) toast.success("Viral share text copied to clipboard!");
      else toast.error("Failed to copy share data.");
    } catch {
      toast.error("Failed to copy share data.");
    }
  };

  // Contextual Share logic for specific article
  const handleCardShare = async (article) => {
    try {
      const articleLink = (article.url && article.url !== "#")
        ? article.url
        : `${window.location.origin}${window.location.pathname}?ref=${article.id}`;
      const shareContent = `${article.title} — ${articleLink}\nArvay - Read between the digital lines`;
      
      logUserActivity("card_share", `${article.source}: ${article.title}`, { source: article.source || "", link: articleLink });
      const ok = await copyToClipboard(shareContent);
      if (ok) toast.success(`Share details for "${article.title.substring(0, 30)}..." copied!`);
      else toast.error("Failed to copy card share details.");
    } catch {
      toast.error("Failed to copy card share details.");
    }
  };

  // Global Email CTA trigger
  const handleEmailAction = () => {
    const subscriptionEl = document.getElementById("newsletter-section");
    if (subscriptionEl) {
      subscriptionEl.scrollIntoView({ behavior: "smooth", block: "center" });
      const emailInput = document.getElementById("newsletter-email");
      if (emailInput) {
        emailInput.focus();
      }
    } else {
      toast.info("Please subscribe using our newsletter desk below.");
    }
  };

  // Handle newsletter subscription
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmittingEmail(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        device_id: deviceId
      });
      if (response.status === 201) {
        logUserActivity("newsletter_submit", email.trim(), { link: email.trim() });
        toast.success("Thank you! You've been successfully added to the early access list.");
        setEmail("");
      }
    } catch (err) {
      console.error("Subscription error:", err);
      const errMsg = err.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(errMsg);
    } finally {
      setSubmittingEmail(false);
    }
  };

  // Handle feedback submission
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackMessage.trim()) {
      toast.error("Please enter your feedback message.");
      return;
    }

    setSubmittingFeedback(true);
    try {
      const payload = {
        message: feedbackMessage.trim(),
        device_id: deviceId,
        ...(feedbackEmail.trim() ? { email: feedbackEmail.trim() } : {})
      };
      
      const response = await axios.post(`${API}/feedback/submit`, payload);
      if (response.status === 201) {
        logUserActivity("feedback_submit", `${feedbackEmail.trim() || "anonymous"}: ${feedbackMessage.trim()}`, {
          keyword: feedbackMessage.trim(),
          link: feedbackEmail.trim() || "anonymous"
        });
        toast.success("Feedback recorded successfully. Thank you for your support!");
        setFeedbackEmail("");
        setFeedbackMessage("");
        setIsFeedbackOpen(false);
      }
    } catch (err) {
      console.error("Feedback error:", err);
      const errMsg = err.response?.data?.detail || "Failed to submit feedback. Please try again.";
      toast.error(errMsg);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] text-[#1A1A1A] font-sans flex flex-col antialiased selection:bg-black selection:text-white">
      {/* Sonner Toaster */}
      <Toaster position="bottom-right" richColors />

      {/* MAIN DASHBOARD CONTAINER */}
      <main className="flex-grow max-w-[1400px] mx-auto w-full px-6 md:px-12 lg:px-20 py-6 md:py-8 flex flex-col justify-between">
        
        {/* TIGHT HEADER HIERARCHY */}
        <div className="space-y-0.5">
          {/* Day and Date in clean small red sans-serif font */}
          <span className="font-sans text-[10px] font-bold text-red-600 uppercase tracking-[0.2em] block">
            {formattedDate}
          </span>
          <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#1A1A1A] font-bold block pt-0.5">
            THE DAILY DESK.
          </span>
          <h1 className="font-serif text-5xl md:text-6xl font-medium tracking-tight text-[#000000] leading-none">
            Arvay
          </h1>
          {/* Tagline with small breathing room */}
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#1A1A1A] pt-1">
            Read between the digital lines.
          </p>
        </div>

        {/* LEFT-ALIGNED GLOBAL ICONS Row (lined up with Arvay branding) */}
        <div data-tour-id="toolbar" className="border-y border-neutral-200/50 py-2.5 my-3 flex items-center justify-start gap-3">

          {/* ── Utility ── */}
          <button
            onClick={handleRefresh}
            className={`p-1.5 text-neutral-400 hover:text-[#000000] transition-colors focus:ring-1 focus:ring-black focus:outline-none ${refreshing ? "animate-spin text-black" : ""}`}
            title="Refresh feeds"
          >
            <RotateCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => onTourStart?.()}
            data-testid="tour-btn"
            className="p-1.5 text-neutral-400 hover:text-[#000000] transition-colors focus:ring-1 focus:ring-black focus:outline-none"
            title="Take a tour"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
          {/* Admin — only for support@arvay.live */}
          {authUser?.email === "support@arvay.live" && (
            <button
              onClick={() => navigate("/admin")}
              data-testid="admin-btn"
              className="p-1.5 text-neutral-400 hover:text-[#000000] transition-colors focus:ring-1 focus:ring-black focus:outline-none"
              title="Admin Panel"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}

          {/* ── Divider ── */}
          <div className="w-px h-4 bg-neutral-200 mx-1" />

          {/* ── Push Notifications bell (logged-in users only) ── */}
          {authUser && (
            <>
              <button
                onClick={handleTogglePush}
                disabled={pushLoading}
                data-testid="push-notifications-btn"
                className={`p-1.5 transition-colors focus:ring-1 focus:ring-black focus:outline-none disabled:opacity-40 ${
                  pushEnabled ? "text-black" : "text-neutral-400 hover:text-[#000000]"
                }`}
                title={pushEnabled ? "Notifications On — click to disable" : "Enable Notifications"}
              >
                {pushEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowPushSettings(p => !p)}
                data-testid="push-settings-btn"
                className={`p-1.5 transition-colors focus:ring-1 focus:ring-black focus:outline-none ${showPushSettings ? "text-black" : "text-neutral-400 hover:text-[#000000]"}`}
                title="Notification tab settings"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
            </>
          )}

          {/* ── Engagement: Early Access | Feedback | Suggest a Source ── */}
          <button
            onClick={handleEmailAction}
            className="p-1.5 text-neutral-400 hover:text-[#000000] transition-colors focus:ring-1 focus:ring-black focus:outline-none"
            title="Join early access"
          >
            <Mail className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              setIsFeedbackOpen(true);
              logUserActivity("feedback_modal_open");
            }}
            data-testid={HOME.feedbackTrigger}
            className="p-1.5 text-neutral-400 hover:text-[#000000] transition-colors focus:ring-1 focus:ring-black focus:outline-none"
            title="Submit feedback"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowSuggest(true)}
            data-testid="suggest-source-btn"
            className="p-1.5 text-neutral-400 hover:text-[#000000] transition-colors focus:ring-1 focus:ring-black focus:outline-none"
            title="Suggest a source"
          >
            <ExternalLink className="w-4 h-4" />
          </button>

          {/* ── Divider ── */}
          <div className="w-px h-4 bg-neutral-200 mx-1" />

          {/* ── Account: Profile | Sign Out ── */}
          <button
            onClick={() => setShowProfile(p => !p)}
            data-testid="profile-btn"
            className={`p-1.5 transition-colors focus:ring-1 focus:ring-black focus:outline-none ${showProfile ? "text-black" : "text-neutral-400 hover:text-[#000000]"}`}
            title={`Profile${authUser?.name ? ` — ${authUser.name}` : ""}`}
          >
            <User className="w-4 h-4" />
          </button>
          {onLogout && (
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="p-1.5 text-neutral-400 hover:text-[#000000] transition-colors focus:ring-1 focus:ring-black focus:outline-none"
              title={`Sign out${authUser?.name ? ` (${authUser.name})` : ""}`}
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}

        </div>

        {/* TABS: Innovation, Global, Regional, Stocks, Horoscope */}
        <div data-tour-id="tab-bar" className="border-b border-neutral-200/50 mb-4">
          <div className="flex gap-6 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  {...(tab === "My Topics" ? { "data-tour-id": "my-topics-tab" } : {})}
                  onClick={() => {
                    setActiveTab(tab);
                    setSearchQuery("");
                    setTopicInput("");
                    logUserActivity("tab_switch", tab);
                    trackGAEvent("page_view", {
                      page_title: `Arvay - ${tab}`,
                      page_location: `${window.location.origin}${window.location.pathname}?tab=${tab}`,
                      page_path: `${window.location.pathname}?tab=${tab}`
                    });
                  }}
                  className={`font-sans text-[10px] uppercase tracking-[0.15em] font-bold pb-2.5 border-b-2 transition-all duration-300 whitespace-nowrap ${
                    isActive
                      ? "border-black text-black"
                      : "border-transparent text-neutral-400 hover:text-neutral-600"
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </div>
        </div>

        {/* REGIONAL: country dropdown */}
        {activeTab === "Regional" && (
          <div className="mb-4 flex items-center gap-3 animate-fade-in">
            <span className="font-sans text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-400 shrink-0">Country</span>
            <select
              data-testid="country-dropdown"
              value={selectedCountry}
              onChange={(e) => {
                setSelectedCountry(e.target.value);
                logUserActivity("country_select", e.target.value);
              }}
              className="font-sans text-xs border border-[#E0E0E0] hover:border-black bg-white px-3 py-2 outline-none transition-colors cursor-pointer"
            >
              {["India","USA","Switzerland","UK","Germany","France","Australia","Singapore","Japan","UAE","Canada","Netherlands","Sweden","Norway","Denmark","Belgium","Austria","Brazil","South Africa","Israel"].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}

        {/* HOROSCOPE: zodiac sign filter chips */}
        {activeTab === "Horoscope" && (
          <div className="mb-4 animate-fade-in">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-sans text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-400">Sign</span>
              {selectedZodiac && (
                <button onClick={() => setSelectedZodiac(null)}
                  className="font-sans text-[10px] text-neutral-400 hover:text-black underline">All Signs</button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {["Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra","Scorpio","Sagittarius","Capricorn","Aquarius","Pisces"].map(sign => (
                <button
                  key={sign}
                  data-testid={`zodiac-${sign.toLowerCase()}`}
                  onClick={() => setSelectedZodiac(selectedZodiac === sign ? null : sign)}
                  className={`font-sans text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 border transition-all duration-200 ${selectedZodiac === sign ? "bg-black text-white border-black" : "border-[#E0E0E0] text-neutral-500 hover:border-black hover:text-black"}`}
                >
                  {sign}
                </button>
              ))}
            </div>
            {/* Daily horoscope reading */}
            {selectedZodiac && (
              <div className="mt-4 border-l-2 border-black bg-white border border-[#E0E0E0]/50 p-4 animate-fade-in" data-testid="daily-horoscope-box">
                {horoscopeLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" />
                    <span className="font-sans text-xs text-neutral-400">Fetching today&apos;s reading...</span>
                  </div>
                ) : dailyHoroscope ? (
                  <>
                    <span className="font-sans text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-400 block mb-1">
                      {dailyHoroscope.sign} · {dailyHoroscope.date || "Today"}
                    </span>
                    <p className="font-sans text-xs text-[#1A1A1A] leading-relaxed">{dailyHoroscope.horoscope}</p>
                  </>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* PUBLICATION STRIP — clickable source filter chips, shown by default */}
        {(() => {
          const tabSources = ALL_SOURCES.filter(s => s.category === activeTab);
          if (tabSources.length === 0) return null;
          return (
            <div className="mb-3 flex flex-wrap gap-1.5 items-center animate-fade-in" data-testid="publication-strip" data-tour-id="source-chips">
              {/* Reset Filters — always first, active only when filters are applied */}
              <button
                onClick={() => setSelectedSources([])}
                data-testid="reset-filters-pill"
                disabled={selectedSources.length === 0}
                className={`font-sans text-[9px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 border transition-all duration-150 flex items-center gap-1 ${
                  selectedSources.length > 0
                    ? "bg-black text-white border-black hover:bg-neutral-800 cursor-pointer"
                    : "bg-transparent text-neutral-300 border-neutral-200 cursor-not-allowed"
                }`}
              >
                <span>× Reset Filters</span>
              </button>
              {tabSources.map(s => (
                <button
                  key={s.name}
                  onClick={() => setSelectedSources(prev =>
                    prev.includes(s.name) ? prev.filter(n => n !== s.name) : [...prev, s.name]
                  )}
                  data-testid={`source-chip-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`font-sans text-[9px] uppercase tracking-[0.15em] font-semibold px-2.5 py-1 border transition-all duration-150 ${selectedSources.includes(s.name) ? "bg-black text-white border-black" : "border-[#E0E0E0] text-neutral-500 hover:border-black hover:text-black"}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          );
        })()}

        {/* SHARES/STOCKS: keyword search over finance feed */}
        {activeTab === "Shares/Stocks" && (
          <div className="mb-4 animate-fade-in">
            <div className="relative flex items-center border border-[#E0E0E0] focus-within:border-black bg-white shadow-sm transition-all">
              <Search className="absolute left-3.5 w-3.5 h-3.5 text-neutral-400" />
              <input
                type="text"
                value={stockQuery}
                onChange={(e) => setStockQuery(e.target.value)}
                placeholder="Search stocks, tickers, companies..."
                data-testid="stock-search-input"
                className="w-full font-sans text-sm bg-transparent pl-10 pr-8 py-2.5 outline-none"
              />
              {stockQuery && (
                <button onClick={() => setStockQuery("")}
                  className="absolute right-3 text-neutral-400 hover:text-black">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* DYNAMIC CATEGORY DESCRIPTION */}
        {(() => {
          const tabSources = ALL_SOURCES.filter(s => s.category === activeTab);
          const shown = tabSources.slice(0, 3).map(s => s.name);
          const more = tabSources.length - shown.length;
          if (tabSources.length === 0) return null;
          return (
            <div className="mb-4 p-3.5 border-l-2 border-black bg-white border border-[#E0E0E0]/30 shadow-xs animate-fade-in">
              <p className="font-sans text-xs md:text-sm text-[#1A1A1A] leading-relaxed font-light">
                {activeTab} news from {shown.join(", ")}{more > 0 ? ` and ${more} more.` : "."}
              </p>
            </div>
          );
        })()}

        {/* SHARES/STOCKS: market index cards + crypto section */}
        {activeTab === "Shares/Stocks" && (
          <div className="mb-6 animate-fade-in space-y-6">
            {/* Index Cards */}
            <div>
              <span className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400 block mb-3">Market Indices · 15-min delay</span>
              {marketLoading ? (
                <div className="flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin text-neutral-400" /><span className="font-sans text-xs text-neutral-400">Fetching indices...</span></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {marketIndices.map(idx => {
                    const isUp = idx.change_pct >= 0;
                    // Build SVG sparkline
                    const pts = idx.chart || [];
                    let sparkline = null;
                    if (pts.length > 1) {
                      const vals = pts.map(p => p.v);
                      const min = Math.min(...vals), max = Math.max(...vals);
                      const range = max - min || 1;
                      const W = 200, H = 40;
                      const coords = vals.map((v, i) => `${(i / (vals.length - 1)) * W},${H - ((v - min) / range) * H}`);
                      sparkline = <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-10" preserveAspectRatio="none"><polyline fill="none" stroke={isUp ? "#16a34a" : "#dc2626"} strokeWidth="1.5" points={coords.join(" ")} /></svg>;
                    }
                    return (
                      <div key={idx.symbol} className="bg-white border border-[#E0E0E0] p-4" data-testid={`index-card-${idx.market}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-bold">{idx.market}</span>
                            <p className="font-serif text-xl font-medium text-black">{idx.name}</p>
                          </div>
                          {!idx.error && (
                            <div className={`text-right`}>
                              <p className="font-sans text-xl font-bold text-black">{idx.price?.toLocaleString("en-IN")}</p>
                              <p className={`font-sans text-xs font-semibold ${isUp ? "text-green-600" : "text-red-600"}`}>
                                {isUp ? "▲" : "▼"} {Math.abs(idx.change_pct)}%
                              </p>
                            </div>
                          )}
                        </div>
                        {sparkline && <div className="mb-3">{sparkline}</div>}
                        {!idx.error && (
                          <div className="grid grid-cols-3 gap-x-3 gap-y-1 mt-2 text-[10px] font-sans text-neutral-500">
                            <span>Open <strong className="text-black">{idx.open?.toLocaleString("en-IN")}</strong></span>
                            <span>High <strong className="text-black">{idx.high?.toLocaleString("en-IN")}</strong></span>
                            <span>Low <strong className="text-black">{idx.low?.toLocaleString("en-IN")}</strong></span>
                            <span>Prev <strong className="text-black">{idx.prev_close?.toLocaleString("en-IN")}</strong></span>
                            <span>52W H <strong className="text-black">{idx.week_52_high?.toLocaleString("en-IN")}</strong></span>
                            <span>52W L <strong className="text-black">{idx.week_52_low?.toLocaleString("en-IN")}</strong></span>
                          </div>
                        )}
                        {idx.error && <p className="font-sans text-xs text-neutral-400 text-center py-2">Data unavailable</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Top Gainers & Decliners */}
            <div>
              <span className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400 block mb-3">Nifty 50 Movers</span>
              {moversLoading ? (
                <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-neutral-400" /><span className="font-sans text-xs text-neutral-400">Loading movers...</span></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Gainers */}
                  <div className="bg-white border border-[#E0E0E0]">
                    <div className="px-4 py-2 border-b border-[#E0E0E0] bg-green-50">
                      <span className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-green-700">Top 10 Gainers</span>
                    </div>
                    {(marketMovers.gainers || []).map((s, i) => (
                      <div key={s.symbol} className="flex items-center justify-between px-4 py-1.5 border-b border-[#E0E0E0]/50 last:border-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-sans text-[9px] text-neutral-300 w-4 shrink-0">{i + 1}</span>
                          <div className="min-w-0">
                            <p className="font-sans text-xs font-semibold text-black truncate">{s.name}</p>
                            <p className="font-sans text-[9px] text-neutral-400">{s.symbol?.replace(".NS", "")}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <p className="font-sans text-xs font-semibold text-black">₹{s.price}</p>
                          <p className="font-sans text-[10px] font-bold text-green-600">▲ {s.change_pct}%</p>
                        </div>
                      </div>
                    ))}
                    {!moversLoading && (marketMovers.gainers || []).length === 0 && <p className="font-sans text-xs text-neutral-400 text-center py-4">Data unavailable</p>}
                  </div>
                  {/* Decliners */}
                  <div className="bg-white border border-[#E0E0E0]">
                    <div className="px-4 py-2 border-b border-[#E0E0E0] bg-red-50">
                      <span className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-red-700">Top 10 Decliners</span>
                    </div>
                    {(marketMovers.decliners || []).map((s, i) => (
                      <div key={s.symbol} className="flex items-center justify-between px-4 py-1.5 border-b border-[#E0E0E0]/50 last:border-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-sans text-[9px] text-neutral-300 w-4 shrink-0">{i + 1}</span>
                          <div className="min-w-0">
                            <p className="font-sans text-xs font-semibold text-black truncate">{s.name}</p>
                            <p className="font-sans text-[9px] text-neutral-400">{s.symbol?.replace(".NS", "")}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <p className="font-sans text-xs font-semibold text-black">₹{s.price}</p>
                          <p className="font-sans text-[10px] font-bold text-red-600">▼ {Math.abs(s.change_pct)}%</p>
                        </div>
                      </div>
                    ))}
                    {!moversLoading && (marketMovers.decliners || []).length === 0 && <p className="font-sans text-xs text-neutral-400 text-center py-4">Data unavailable</p>}
                  </div>
                </div>
              )}
            </div>

            {/* Crypto Section */}
            <div>
              <span className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400 block mb-3">Crypto · Top 10 by Market Cap</span>
              {cryptoLoading ? (
                <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-neutral-400" /><span className="font-sans text-xs text-neutral-400">Loading crypto...</span></div>
              ) : cryptoError ? (
                <p className="font-sans text-xs text-neutral-400">Crypto data unavailable. Try refreshing.</p>
              ) : (
                <div className="bg-white border border-[#E0E0E0] overflow-hidden">
                  {cryptoCoins.map((c, i) => {
                    const isUp = c.change_24h >= 0;
                    return (
                      <div key={c.id} className="flex items-center justify-between px-4 py-2.5 border-b border-[#E0E0E0]/50 last:border-0 hover:bg-neutral-50 transition-colors" data-testid={`crypto-row-${c.symbol}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-sans text-[10px] text-neutral-300 w-4 shrink-0 text-right">{i + 1}</span>
                          <img src={c.image} alt={c.name} className="w-5 h-5 rounded-full shrink-0" />
                          <div className="min-w-0">
                            <p className="font-sans text-sm font-semibold text-black">{c.name}</p>
                            <p className="font-sans text-[9px] text-neutral-400 uppercase tracking-wider">{c.symbol}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <p className="font-sans text-sm font-semibold text-black">${c.price < 1 ? c.price.toFixed(4) : c.price.toLocaleString()}</p>
                          <p className={`font-sans text-[10px] font-bold ${isUp ? "text-green-600" : "text-red-600"}`}>{isUp ? "▲" : "▼"} {Math.abs(c.change_24h)}%</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* COMMUNITY: Reddit / YouTube sub-tabs */}
        {activeTab === "Community" && (
          <div className="mb-4 animate-fade-in">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-sans text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-400 shrink-0">Source</span>
              <div className="flex gap-2">
                {[{ key: "reddit", label: "Reddit" }, { key: "youtube", label: "YouTube" }, { key: "general", label: "General" }].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setCommunitySubTab(key)}
                    data-testid={`community-subtab-${key}`}
                    className={`font-sans text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 border transition-all duration-200 ${communitySubTab === key ? "bg-black text-white border-black" : "border-[#E0E0E0] text-neutral-500 hover:border-black hover:text-black"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <p className="font-sans text-xs text-neutral-400 font-light">
              {communitySubTab === "reddit"
                ? "Top posts from r/worldnews, r/business, r/stocks, r/india, r/technology"
                : communitySubTab === "youtube"
                ? "Latest videos from BBC News, Al Jazeera, DW News, and NDTV on YouTube"
                : "Search Reddit across all subreddits using the keyword above"}
            </p>
          </div>
        )}

        {/* KEYWORD FILTER CHIPS — shown on all tabs for logged-in users */}
        {authUser && (
          <div className="mb-4 animate-fade-in" data-testid="tab-keyword-filter">
            {(() => {
              const currentTabKws = userTopics.filter(t => (t.tab || "My Topics") === activeTab);
              return (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="relative flex-1 flex items-center border border-[#E0E0E0] focus-within:border-black bg-white shadow-sm transition-all">
                      <input
                        type="text"
                        value={topicInput}
                        onChange={(e) => setTopicInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") addTopic(topicInput); }}
                        placeholder={activeTab === "My Topics" ? "Add a keyword (e.g. AI, climate, India)" : `Filter ${activeTab} by keyword...`}
                        data-testid="topic-input"
                        className="w-full font-sans text-sm bg-transparent px-4 py-2 outline-none"
                      />
                    </div>
                    <button
                      onClick={() => addTopic(topicInput)}
                      disabled={!topicInput.trim()}
                      data-testid="topic-add-btn"
                      className="font-sans text-[10px] uppercase tracking-[0.18em] font-semibold px-4 py-2 bg-black text-white hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {currentTabKws.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-1">
                      {currentTabKws.map((t) => (
                        <span key={t.id} data-testid={`keyword-chip-${t.keyword}`} className="flex items-center gap-1.5 font-sans text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 bg-black text-white">
                          {t.keyword}
                          <button onClick={() => removeTopic(t.id, t.keyword)} className="ml-0.5 hover:text-neutral-300 transition-colors focus:outline-none" data-testid={`remove-keyword-${t.keyword}`}>
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {activeTab === "My Topics" && topicsLoading && (
                    <div className="flex items-center gap-2 mt-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" />
                      <span className="font-sans text-xs text-neutral-400">Loading your feed...</span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* 2-3 COLUMNS RESPONSIVE GRID LAYOUT */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 animate-fade-in" key={`${activeTab}-${selectedCountry}-${selectedZodiac}`}>
          {loadingSearch && searchQuery.trim() ? (
            /* Searching... spinner shown during active search */
            <div className="col-span-full flex flex-col items-center justify-center py-24">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-300 mb-5" />
              <p className="font-sans text-sm text-neutral-500 tracking-wide" data-testid="search-loading-msg">
                Searching...
              </p>
            </div>
          ) : refreshing || loadingFeed ? (
            <div className="col-span-full flex flex-col items-center justify-center py-20 bg-white border border-[#E0E0E0]/70 shadow-xs">
              <Loader2 className="w-8 h-8 animate-spin text-neutral-400 mb-4" />
              <p className="font-serif text-sm font-medium">
                {activeTab === "Community" ? "Loading community posts..." : "Re-indexing feed..."}
              </p>
            </div>
          ) : (() => {
            let articlesToShow = filteredNews;
            // Shares/Stocks tab: apply stock keyword filter
            if (activeTab === "Shares/Stocks" && stockQuery.trim()) {
              const sq = stockQuery.trim().toLowerCase();
              articlesToShow = articlesToShow.filter(a =>
                (a.title || "").toLowerCase().includes(sq) || (a.description || "").toLowerCase().includes(sq)
              );
            }
            // Horoscope tab: apply zodiac sign filter
            if (activeTab === "Horoscope" && selectedZodiac) {
              const sign = selectedZodiac.toLowerCase();
              articlesToShow = articlesToShow.filter(a =>
                (a.title || "").toLowerCase().includes(sign) || (a.description || "").toLowerCase().includes(sign)
              );
            }
            if (articlesToShow.length > 0) { return articlesToShow.slice(0, displayCount).map((news) => {
              const hasLink = news.url && news.url !== "#";
              const timeLabel = news.time || formatRelativeTime(news.published);
              return (
              <div 
                key={news.id} 
                onClick={() => {
                  if (hasLink) {
                    window.open(news.url, "_blank", "noopener,noreferrer");
                    logUserActivity("card_click", `${news.source}: ${news.title}`);
                  }
                }}
                data-testid={`news-card-${news.id}`}
                className={`bg-white border border-[#E0E0E0]/70 p-6 hover:border-black/30 transition-all duration-300 hover:-translate-y-[2px] hover:shadow-md flex flex-col justify-between gap-5 group relative shadow-xs hover:shadow-sm ${hasLink ? "cursor-pointer" : ""}`}
              >
                <div>
                  {/* Article thumbnail (when the source provides one) */}
                  {news.image ? (
                    <div className="mb-4 -mx-6 -mt-6 aspect-[16/9] overflow-hidden bg-neutral-100 border-b border-[#E0E0E0]/70 relative">
                      <img
                        src={news.image}
                        alt={news.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        onError={(e) => { const p = e.currentTarget.parentElement; if (p) p.style.display = "none"; }}
                      />
                      {news.communityType === "youtube" && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-10 h-10 bg-red-600/90 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 fill-white ml-0.5" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                  {/* Card Metadata and Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 max-w-[70%]">
                      {/* Source badge: Reddit=orange, YouTube=red, default=black */}
                      {news.communityType === "reddit" ? (
                        <span className="font-sans text-[10px] font-bold bg-orange-500 text-white px-2.5 py-0.5 tracking-wider uppercase shrink-0">
                          {news.source?.toLowerCase()}
                        </span>
                      ) : news.communityType === "youtube" ? (
                        <span className="font-sans text-[10px] font-bold bg-red-600 text-white px-2.5 py-0.5 tracking-wider uppercase shrink-0 flex items-center gap-1">
                          <svg className="w-2.5 h-2.5 fill-white" viewBox="0 0 24 24"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.8 15.5V8.5l6.2 3.5-6.2 3.5z"/></svg>
                          {news.source?.replace(" YT","").toLowerCase()}
                        </span>
                      ) : (
                        <span className="font-sans text-[10px] font-bold bg-black text-white px-2.5 py-0.5 tracking-wider uppercase shrink-0">
                          {news.source}
                        </span>
                      )}
                      {news.author && (
                        <span className="font-sans text-[10px] text-neutral-500 uppercase tracking-wider font-semibold truncate" title={news.author}>
                          By {news.author}
                        </span>
                      )}
                    </div>
                    <span className="font-sans text-[10px] text-neutral-400 font-light">
                      {timeLabel}
                    </span>
                  </div>

                  {/* Editorial Headline: clickable hyperlink when URL is available */}
                  {hasLink ? (
                    <a
                      href={news.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="font-serif text-base md:text-lg font-medium text-[#000000] leading-snug mb-2 hover:underline hover:text-neutral-700 transition-colors line-clamp-2 min-h-[3rem] block"
                    >
                      <HighlightText text={news.title} query={searchQuery} />
                    </a>
                  ) : (
                    <h3 className="font-serif text-base md:text-lg font-medium text-[#000000] leading-snug mb-2 group-hover:text-neutral-800 transition-colors line-clamp-2 min-h-[3rem]">
                      <HighlightText text={news.title} query={searchQuery} />
                    </h3>
                  )}

                  {/* Detailed Description: Deep Charcoal #1A1A1A */}
                  <p className="font-sans text-xs text-[#1A1A1A] leading-relaxed font-light line-clamp-3">
                    <HighlightText text={news.description} query={searchQuery} />
                  </p>
                </div>

                {/* Card footer: Reading Time, Share, Bookmark, Report, Chevron */}
                <div className="flex items-center justify-between pt-3.5 border-t border-neutral-100/50">
                  <div className="flex items-center gap-1.5 text-neutral-400 text-[11px] font-light">
                    <Clock className="w-3.5 h-3.5 text-neutral-400/80" />
                    <span>{news.readTime}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Bookmark */}
                    {hasLink && (
                      <button
                        onClick={async (e) => { e.stopPropagation(); await toggleBookmark(news); }}
                        className="p-1.5 transition-all duration-200 rounded-full hover:bg-neutral-50 focus:ring-1 focus:ring-black focus:outline-none"
                        title={isBookmarked(news.url) ? "Remove bookmark" : "Save article"}
                        data-testid={`bookmark-btn-${news.id}`}
                      >
                        {isBookmarked(news.url)
                          ? <BookmarkCheck className="w-3.5 h-3.5 text-black" />
                          : <Bookmark className="w-3.5 h-3.5 text-neutral-400 hover:text-black" />
                        }
                      </button>
                    )}
                    {/* Share (Web Share API) */}
                    <button
                      onClick={async (e) => { e.stopPropagation(); await shareArticle(news); }}
                      className="p-1.5 text-neutral-400 hover:text-black transition-all duration-200 rounded-full hover:bg-neutral-50 focus:ring-1 focus:ring-black focus:outline-none"
                      title="Share this article"
                      data-testid={`share-btn-${news.id}`}
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                    {/* Report */}
                    <button
                      onClick={(e) => { e.stopPropagation(); openReportModal(news); }}
                      className="p-1.5 text-neutral-400 hover:text-red-500 transition-all duration-200 rounded-full hover:bg-red-50 focus:ring-1 focus:ring-red-400 focus:outline-none"
                      title="Report this article"
                      data-testid={`report-btn-${news.id}`}
                    >
                      <Flag className="w-3.5 h-3.5" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-neutral-300 group-hover:translate-x-1 group-hover:text-black transition-all duration-300" />
                  </div>
                </div>
              </div>
              );
            })} else {
              const currentTabKws = userTopics.filter(t => (t.tab || "My Topics") === activeTab);
              const myTopicsKws = userTopics.filter(t => (t.tab || "My Topics") === "My Topics");
              if (activeTab === "My Topics" && myTopicsKws.length === 0) {
                return (
                  <div className="col-span-full flex flex-col items-center justify-center py-20 text-center px-6">
                    <p className="font-sans text-xs text-neutral-400 max-w-sm mx-auto leading-relaxed">
                      Add a keyword above to start building your personal news feed.
                    </p>
                  </div>
                );
              }
              if (currentTabKws.length > 0 && activeTab !== "My Topics") {
                return (
                  <div className="col-span-full flex flex-col items-center justify-center py-20 text-center bg-white border border-[#E0E0E0] shadow-xs px-6">
                    <Search className="w-10 h-8 text-neutral-400 mb-4" />
                    <h4 className="font-serif text-lg font-medium text-[#000000] mb-2">No articles match your filters</h4>
                    <p className="font-sans text-xs text-[#1A1A1A] max-w-sm mx-auto leading-relaxed mb-4">
                      No {activeTab} articles matched your keyword filters. Try different keywords or clear them.
                    </p>
                    <button
                      onClick={async () => {
                        for (const t of currentTabKws) await removeTopic(t.id, t.keyword);
                      }}
                      className="font-sans text-xs uppercase tracking-[0.15em] font-semibold bg-black text-white py-3 px-6 hover:bg-neutral-800 transition-all rounded-none focus:ring-1 focus:ring-black focus:outline-none"
                    >
                      Clear Keywords
                    </button>
                  </div>
                );
              }
              return (
                <div className="col-span-full flex flex-col items-center justify-center py-20 text-center bg-white border border-[#E0E0E0] shadow-xs px-6">
                  <Search className="w-10 h-8 text-neutral-400 mb-4" />
                  {activeTab === "Community" && communitySubTab === "general" && !searchQuery.trim() ? (
                    <>
                      <h4 className="font-serif text-lg font-medium text-[#000000] mb-2">Search Reddit across all communities</h4>
                      <p className="font-sans text-xs text-[#1A1A1A] max-w-sm mx-auto leading-relaxed">
                        Enter a keyword in the filter above to find posts from any subreddit.
                      </p>
                    </>
                  ) : activeTab === "Community" && communitySubTab === "general" && redditUnavailable ? (
                    <>
                      <h4 className="font-serif text-lg font-medium text-[#000000] mb-2">Reddit is temporarily unavailable</h4>
                      <p className="font-sans text-xs text-[#1A1A1A] max-w-sm mx-auto leading-relaxed">
                        Reddit is not returning results at this time. Please try again shortly.
                      </p>
                    </>
                  ) : searchQuery.trim() ? (
                    <>
                      <h4 className="font-serif text-lg font-medium text-[#000000] mb-2">No results found for &ldquo;{searchQuery}&rdquo;</h4>
                      <p className="font-sans text-xs text-[#1A1A1A] max-w-sm mx-auto leading-relaxed">
                        Try a different keyword, adjust your source filters, or check back soon.
                      </p>
                      <button onClick={() => setSearchQuery("")}
                        className="mt-6 font-sans text-xs uppercase tracking-[0.15em] font-semibold bg-black text-white py-3 px-6 hover:bg-neutral-800 transition-all duration-300 rounded-none focus:ring-1 focus:ring-black focus:outline-none">
                        Clear Search
                      </button>
                    </>
                  ) : (
                    <>
                      <h4 className="font-serif text-lg font-medium text-[#000000] mb-2">Nothing here yet</h4>
                      <p className="font-sans text-xs text-[#1A1A1A] max-w-sm mx-auto leading-relaxed">
                        Try a different keyword or check back soon.
                      </p>
                    </>
                  )}
                </div>
              );
            }
          })()}
        </div>

        {/* LOAD MORE: only visible when there are more articles to show */}
        {filteredNews.length > displayCount && (
          <div className="flex justify-center mb-10 -mt-4">
            <button
              data-testid="load-more-btn"
              onClick={() => setDisplayCount(c => c + 10)}
              className="font-sans text-xs uppercase tracking-[0.15em] font-semibold border border-black/20 hover:border-black text-[#1A1A1A] py-3 px-10 hover:bg-black hover:text-white transition-all duration-300 rounded-none focus:ring-1 focus:ring-black focus:outline-none flex items-center gap-2"
            >
              Load More
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div id="newsletter-section" className="border border-[#E0E0E0] bg-white p-8 md:p-12 mb-8 flex flex-col md:flex-row justify-between items-center gap-8 shadow-xs">
          <div className="max-w-md">
            {/* Get Arvay For Free, only Arvay bold */}
            <h4 className="text-xl mb-1.5 text-[#000000] font-sans">
              Get <span className="font-bold">Arvay</span> For Free
            </h4>
            <p className="font-sans text-xs text-[#1A1A1A] leading-relaxed">
              Join the Exclusive Early Access List
            </p>
          </div>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 w-full max-w-lg">
            <input
              id="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@organization.com"
              required
              data-testid={HOME.newsletterEmailInput}
              disabled={submittingEmail}
              className="flex-grow font-sans text-sm bg-[#FAFAFA] border border-black/10 focus:border-black py-3.5 px-4.5 rounded-none transition-colors duration-300 outline-none focus:ring-1 focus:ring-black"
            />
            <button
              type="submit"
              disabled={submittingEmail}
              data-testid={HOME.newsletterSubmitButton}
              className="font-sans text-xs uppercase tracking-[0.15em] font-semibold bg-black text-white py-4 px-6 hover:bg-neutral-800 transition-all duration-300 rounded-none flex items-center justify-center gap-2 min-w-[220px] focus:ring-1 focus:ring-black focus:outline-none disabled:opacity-75"
            >
              {submittingEmail ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Request Invitation
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* INTEGRATED SOURCES PANEL — publication names & logos, no URLs */}
        <SourcesPanel />

        {/* BOTTOM DASHBOARD FOOTER */}
        <footer className="pt-8 border-t border-neutral-200/50 mt-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 pb-8 border-b border-neutral-200/30">
            {/* Column 1: Editorial Note */}
            <div>
              <h5 className="font-serif text-lg font-medium text-[#000000] mb-2.5">Arvay</h5>
              <p className="font-sans text-xs text-[#1A1A1A] leading-relaxed font-light">
                Authoritative media monitoring and global narrative tracking. Providing un-amplified signal intelligence for sovereign institutions and decision makers.
              </p>
            </div>

            {/* Column 2: Dedicated Contact Us compliance section */}
            <div>
              <h5 className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-[#000000] mb-3">Contact Us</h5>
              <ul className="space-y-2">
                <li className="font-sans text-xs text-[#1A1A1A]">
                  <span className="font-semibold">Email:</span> <a href="mailto:support@arvay.live" className="underline hover:text-black transition-colors">support@arvay.live</a>
                </li>

              </ul>
            </div>

            {/* Column 3: Corporate Info */}
            <div>
              <h5 className="font-sans text-[10px] uppercase tracking-[0.2em] font-bold text-[#000000] mb-3">Compliance</h5>
              <p className="font-sans text-xs text-[#1A1A1A] leading-relaxed font-light">
                All media streams are processed with zero-knowledge, privacy-first ingestion nodes. Certified for Google Play compliance.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end items-center gap-4 pb-6">
            <div className="flex items-center gap-4">
              <a
                href="/privacy"
                className="font-sans text-xs text-neutral-500 underline hover:text-black transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/disclaimer"
                className="font-sans text-xs text-neutral-500 underline hover:text-black transition-colors"
              >
                Legal &amp; Source Disclaimer
              </a>
            </div>
          </div>
        </footer>
      </main>

      {/* CRYSTAL GLASS FEEDBACK POPUP MODAL */}
      {isFeedbackOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xl animate-fade-in"
          data-testid={HOME.feedbackModal}
        >
          <div className="relative w-full max-w-lg bg-white border border-black/10 p-8 md:p-12 shadow-2xl rounded-none">
            {/* Close Trigger */}
            <button
              onClick={() => setIsFeedbackOpen(false)}
              className="absolute top-6 right-6 p-1 text-neutral-400 hover:text-black transition-colors focus:ring-1 focus:ring-black focus:outline-none"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-8">
              <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#888888] font-bold block mb-3">
                FEEDBACK PLATFORM
              </span>
              <h3 className="font-serif text-3xl font-medium text-[#000000]">
                Share Your Insights
              </h3>
              <p className="font-sans text-xs text-[#1A1A1A] mt-2 leading-relaxed font-light">
                Help us craft Arvay&apos;s future. Let us know which analytics or security modules you&apos;d like to see prioritized on the dashboard. To suggest a new publication, use the{" "}
                <button type="button" onClick={() => { setIsFeedbackOpen(false); setShowSuggest(true); }}
                  className="underline hover:text-black transition-colors">
                  Suggest a Source
                </button>{" "}
                button in the toolbar.
              </p>
            </div>

            <form onSubmit={handleFeedbackSubmit} className="space-y-6">
              {/* Optional Email */}
              <div>
                <label className="block font-sans text-[10px] uppercase tracking-[0.15em] font-medium text-[#555555] mb-2">
                  Email address (Optional)
                </label>
                <input
                  type="email"
                  value={feedbackEmail}
                  onChange={(e) => setFeedbackEmail(e.target.value)}
                  placeholder="name@organization.com"
                  data-testid={HOME.feedbackEmailInput}
                  disabled={submittingFeedback}
                  className="w-full font-sans text-sm bg-[#FAFAFA] border border-black/10 focus:border-black py-3.5 px-4 rounded-none transition-colors duration-300 outline-none focus:ring-1 focus:ring-black"
                />
              </div>

              {/* Required Message */}
              <div>
                <label className="block font-sans text-[10px] uppercase tracking-[0.15em] font-medium text-[#555555] mb-2">
                  Your feedback message
                </label>
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  placeholder="What dashboard utilities or news streams would be most valuable to you?"
                  required
                  rows={4}
                  data-testid={HOME.feedbackMessageInput}
                  disabled={submittingFeedback}
                  className="w-full font-sans text-sm bg-[#FAFAFA] border border-black/10 focus:border-black py-3.5 px-4 rounded-none transition-colors duration-300 outline-none focus:ring-1 focus:ring-black resize-none"
                />
              </div>

              {/* Submit Feedback */}
              <button
                type="submit"
                disabled={submittingFeedback}
                data-testid={HOME.feedbackSubmitButton}
                className="w-full font-sans text-xs uppercase tracking-[0.15em] font-medium bg-black text-white py-4 hover:bg-neutral-800 transition-all duration-300 rounded-none flex items-center justify-center gap-2 focus:ring-1 focus:ring-black focus:outline-none disabled:opacity-75"
              >
                {submittingFeedback ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Submit Feedback
                    <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* EXIT CONFIRMATION DIALOG */}
      {showExitDialog && (
        <div
          data-testid="exit-dialog"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-sm bg-white border border-black/10 p-8 shadow-2xl">
            <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-bold block mb-3">
              Just a moment
            </span>
            <h3 className="font-serif text-2xl font-medium text-[#000000] mb-2">
              Leave Arvay?
            </h3>
            <p className="font-sans text-xs text-[#1A1A1A] leading-relaxed mb-7">
              Your session and recent searches are saved and will be here when you return.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExitDialog(false)}
                data-testid="exit-dialog-stay"
                className="flex-1 bg-black text-white font-sans text-xs uppercase tracking-[0.15em] font-semibold py-3.5 hover:bg-neutral-800 transition-all focus:outline-none"
              >
                Stay
              </button>
              <button
                onClick={() => {
                  setShowExitDialog(false);
                  // Go back 2 entries: our guard entry + the original previous page
                  window.history.go(-2);
                }}
                data-testid="exit-dialog-leave"
                className="flex-1 border border-black/20 hover:border-black font-sans text-xs uppercase tracking-[0.15em] font-semibold py-3.5 text-[#1A1A1A] hover:bg-neutral-50 transition-all focus:outline-none flex items-center justify-center gap-2"
              >
                <LogOut className="w-3.5 h-3.5" />
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* USER PROFILE PANEL */}
      {showProfile && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowProfile(false)}
          data-testid="profile-modal"
        >
          <div
            className="w-full max-w-sm bg-white border border-black/10 p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-bold block mb-0.5">
                  Your Account
                </span>
                <h3 className="font-serif text-2xl font-medium text-[#000000]">Profile</h3>
              </div>
              <button onClick={() => setShowProfile(false)} className="p-1 text-neutral-400 hover:text-black transition-colors focus:outline-none">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mb-5 pb-5 border-b border-neutral-100">
              <p className="font-sans text-xs text-neutral-500 mb-0.5">Signed in as</p>
              <p className="font-sans text-sm font-medium text-[#1A1A1A] truncate">{authUser?.email}</p>
              {authUser?.name && (
                <p className="font-sans text-xs text-neutral-400 mt-0.5">{authUser.name}</p>
              )}
            </div>
            <div className="space-y-3">
              <label className="block font-sans text-[10px] uppercase tracking-[0.15em] font-medium text-neutral-500">
                Update Display Name
              </label>
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder={authUser?.name || "Your name"}
                data-testid="profile-name-input"
                className="w-full font-sans text-sm bg-[#FAFAFA] border border-black/10 focus:border-black py-3 px-4 rounded-none transition-colors outline-none focus:ring-1 focus:ring-black"
                onKeyDown={(e) => { if (e.key === "Enter") updateDisplayName(); }}
              />
              <button
                onClick={updateDisplayName}
                data-testid="profile-save-btn"
                className="w-full font-sans text-xs uppercase tracking-[0.15em] font-semibold bg-black text-white py-3.5 hover:bg-neutral-800 transition-all rounded-none focus:outline-none"
              >
                Save Name
              </button>
            </div>
            <div className="mt-5 pt-5 border-t border-neutral-100 space-y-2">
              <button
                onClick={() => { setShowProfile(false); setShowBookmarksPage(true);
                // Fetch full bookmark objects
                axios.get(`${API}/bookmarks`, { withCredentials: true })
                  .then(r => setFullBookmarks(r.data.bookmarks || []))
                  .catch(() => setFullBookmarks([]));
              }}
                data-testid="view-bookmarks-btn"
                className="w-full font-sans text-xs uppercase tracking-[0.15em] font-semibold border border-[#E0E0E0] text-[#1A1A1A] py-2.5 hover:border-black transition-all rounded-none focus:outline-none flex items-center justify-center gap-2"
              >
                <Bookmark className="w-3.5 h-3.5" />
                View Bookmarks ({bookmarks.length})
              </button>
              <p className="font-sans text-[10px] text-neutral-400 text-center">
                {bookmarks.length} saved article{bookmarks.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* PUSH NOTIFICATION SETTINGS PANEL — left side */}
      {showPushSettings && authUser && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-start justify-start bg-black/40 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowPushSettings(false)}
          data-testid="push-settings-panel"
        >
          <div
            className="w-full max-w-sm h-full bg-white border-r border-black/10 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-7">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-bold block mb-0.5">Alerts</span>
                  <h3 className="font-serif text-2xl font-medium text-[#000000]">Notification Settings</h3>
                </div>
                <button onClick={() => setShowPushSettings(false)} className="p-1 text-neutral-400 hover:text-black transition-colors focus:outline-none">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {!pushEnabled && (
                <div className="mb-4 p-3 border border-amber-200 bg-amber-50">
                  <p className="font-sans text-xs text-amber-700 leading-relaxed">Push notifications are off. Enable them via the bell icon to receive alerts.</p>
                </div>
              )}

              {/* Tab toggles */}
              <p className="font-sans text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-400 mb-2">Alert by Tab</p>
              <div className="space-y-1 mb-5">
                {["My Topics", "Innovation", "Global", "Regional", "Shares/Stocks", "Horoscope", "Community"].map(tab => {
                  const isOn = pushTabSettings[tab] ?? (tab === "My Topics");
                  const tabKws = userTopics.filter(t => (t.tab || "My Topics") === tab);
                  return (
                    <div key={tab} className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
                      <div className="flex flex-col">
                        <span className="font-sans text-sm font-medium text-[#1A1A1A]">{tab}</span>
                        <span className="font-sans text-[10px] text-neutral-400">
                          {tabKws.map(kw => {
                            const count = pushKwStats[kw.keyword.toLowerCase()] || 0;
                            return (
                              <span key={kw.id} className="mr-2">
                                {kw.keyword}{count > 0 ? <span className="text-neutral-500"> · {count} today</span> : null}
                              </span>
                            );
                          })}
                          {tabKws.length === 0 && <span className="italic">no keywords</span>}
                        </span>
                      </div>
                      <button
                        data-testid={`push-toggle-${tab.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                        onClick={async () => {
                          const ns = { ...pushTabSettings, [tab]: !isOn };
                          setPushTabSettings(ns);
                          try { await axios.put(`${API}/push/settings`, { settings: ns }, { withCredentials: true }); }
                          catch { toast.error("Could not save settings"); }
                        }}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${isOn ? "bg-black" : "bg-neutral-200"}`}
                        role="switch" aria-checked={isOn}
                      >
                        <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform ${isOn ? "translate-x-4" : "translate-x-0"}`} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Min gap per keyword */}
              <p className="font-sans text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-400 mb-2">Min Gap Between Alerts</p>
              <div className="mb-5">
                <p className="font-sans text-xs text-neutral-500 mb-2">Maximum one alert per keyword within the selected window, even if multiple sources cover the same story.</p>
                <div className="flex gap-2 flex-wrap">
                  {[0, 1, 2, 4, 6].map(h => {
                    const isActive = (pushTabSettings.min_gap_hours ?? 1) === h;
                    return (
                      <button
                        key={h}
                        data-testid={`min-gap-${h}`}
                        onClick={async () => {
                          const ns = { ...pushTabSettings, min_gap_hours: h };
                          setPushTabSettings(ns);
                          try { await axios.put(`${API}/push/settings`, { settings: ns }, { withCredentials: true }); }
                          catch { toast.error("Could not save settings"); }
                        }}
                        className={`font-sans text-xs px-3 py-1.5 border transition-colors ${isActive ? "bg-black text-white border-black" : "border-neutral-200 text-neutral-600 hover:border-black"}`}
                      >
                        {h === 0 ? "No limit" : `${h}h`}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quiet hours */}
              <p className="font-sans text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-400 mb-2">Quiet Hours</p>
              <div className="mb-4">
                <p className="font-sans text-xs text-neutral-500 mb-3">Notifications during this window are queued and delivered as a single batch when quiet hours end.</p>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-sans text-sm text-[#1A1A1A]">Enable quiet hours</span>
                  <button
                    data-testid="quiet-hours-toggle"
                    onClick={async () => {
                      const ns = { ...pushTabSettings, quiet_hours_enabled: !pushTabSettings.quiet_hours_enabled };
                      setPushTabSettings(ns);
                      try { await axios.put(`${API}/push/settings`, { settings: ns }, { withCredentials: true }); }
                      catch { toast.error("Could not save settings"); }
                    }}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${pushTabSettings.quiet_hours_enabled ? "bg-black" : "bg-neutral-200"}`}
                    role="switch" aria-checked={pushTabSettings.quiet_hours_enabled}
                  >
                    <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform ${pushTabSettings.quiet_hours_enabled ? "translate-x-4" : "translate-x-0"}`} />
                  </button>
                </div>
                {pushTabSettings.quiet_hours_enabled && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="font-sans text-[10px] uppercase tracking-[0.15em] text-neutral-400 block mb-1">From</label>
                      <input type="time" value={pushTabSettings.quiet_hours_start || "23:00"}
                        onChange={async (e) => {
                          const ns = { ...pushTabSettings, quiet_hours_start: e.target.value };
                          setPushTabSettings(ns);
                          try { await axios.put(`${API}/push/settings`, { settings: ns }, { withCredentials: true }); }
                          catch {}
                        }}
                        className="w-full font-sans text-sm border border-neutral-200 px-2 py-1.5 focus:border-black focus:outline-none"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="font-sans text-[10px] uppercase tracking-[0.15em] text-neutral-400 block mb-1">Until</label>
                      <input type="time" value={pushTabSettings.quiet_hours_end || "07:00"}
                        onChange={async (e) => {
                          const ns = { ...pushTabSettings, quiet_hours_end: e.target.value };
                          setPushTabSettings(ns);
                          try { await axios.put(`${API}/push/settings`, { settings: ns }, { withCredentials: true }); }
                          catch {}
                        }}
                        className="w-full font-sans text-sm border border-neutral-200 px-2 py-1.5 focus:border-black focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>
              <p className="font-sans text-[10px] text-neutral-400 text-center mt-4 leading-relaxed">Changes save instantly.</p>
            </div>
          </div>
        </div>
      )}

      {/* SUGGEST A SOURCE MODAL */}
      {showSuggest && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-xl animate-fade-in"
          onClick={() => setShowSuggest(false)}
          data-testid="suggest-modal"
        >
          <div
            className="w-full max-w-md bg-white border border-black/10 p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-bold block mb-0.5">
                  Editorial Desk
                </span>
                <h3 className="font-serif text-2xl font-medium text-[#000000]">Suggest a Source</h3>
              </div>
              <button onClick={() => setShowSuggest(false)} className="p-1 text-neutral-400 hover:text-black transition-colors focus:outline-none">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="font-sans text-xs text-[#1A1A1A] leading-relaxed mb-6">
              Know a publication Arvay should track? Submit it for editorial review.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block font-sans text-[10px] uppercase tracking-[0.15em] font-medium text-neutral-500 mb-2">
                  Publication Name
                </label>
                <input
                  type="text"
                  value={suggestName}
                  onChange={(e) => setSuggestName(e.target.value)}
                  placeholder="e.g. The Economist"
                  data-testid="suggest-name-input"
                  className="w-full font-sans text-sm bg-[#FAFAFA] border border-black/10 focus:border-black py-3 px-4 rounded-none transition-colors outline-none focus:ring-1 focus:ring-black"
                />
              </div>
              <div>
                <label className="block font-sans text-[10px] uppercase tracking-[0.15em] font-medium text-neutral-500 mb-2">
                  RSS / Website URL
                </label>
                <input
                  type="url"
                  value={suggestUrl}
                  onChange={(e) => setSuggestUrl(e.target.value)}
                  placeholder="https://www.example.com/rss"
                  data-testid="suggest-url-input"
                  className="w-full font-sans text-sm bg-[#FAFAFA] border border-black/10 focus:border-black py-3 px-4 rounded-none transition-colors outline-none focus:ring-1 focus:ring-black"
                />
              </div>
              <button
                onClick={submitSuggestion}
                data-testid="suggest-submit-btn"
                className="w-full font-sans text-xs uppercase tracking-[0.15em] font-semibold bg-black text-white py-3.5 hover:bg-neutral-800 transition-all rounded-none focus:outline-none flex items-center justify-center gap-2"
              >
                Submit for Review
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
              <p className="font-sans text-[10px] text-neutral-400 text-center leading-relaxed pt-1">
                We'll review your suggestion and try to add it. If the source has an accessible RSS feed, it will be added in the next update.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* BOOKMARKS PAGE OVERLAY */}
      {showBookmarksPage && (
        <div className="fixed inset-0 z-[70] bg-white flex flex-col animate-fade-in overflow-auto" data-testid="bookmarks-page">
          <div className="sticky top-0 bg-white border-b border-[#E0E0E0] px-6 py-4 flex items-center justify-between z-10">
            <div>
              <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-bold block mb-0.5">Your Library</span>
              <h2 className="font-serif text-2xl font-medium text-[#000000]">Saved Articles</h2>
            </div>
            <button onClick={() => setShowBookmarksPage(false)} className="p-2 text-neutral-400 hover:text-black transition-colors focus:outline-none" data-testid="bookmarks-close-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="max-w-3xl mx-auto w-full px-6 py-6">
            {fullBookmarks.length === 0 ? (
              <div className="text-center py-24">
                <Bookmark className="w-8 h-8 text-neutral-200 mx-auto mb-4" />
                <p className="font-sans text-sm text-neutral-400">No saved articles yet. Bookmark articles to see them here.</p>
              </div>
            ) : (
              <div className="space-y-0 border border-[#E0E0E0]">
                {fullBookmarks.map((bm, idx) => (
                  <div key={bm.url || idx} className="flex items-start gap-4 p-4 border-b border-[#E0E0E0]/50 last:border-0 hover:bg-neutral-50 transition-colors group" data-testid={`bookmark-item-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-sans text-[9px] uppercase tracking-[0.18em] font-bold text-neutral-400 block mb-1">{bm.source}</span>
                      {bm.url ? (
                        <a href={bm.url} target="_blank" rel="noopener noreferrer" className="font-serif text-base font-medium text-[#000000] leading-snug hover:underline block">
                          {bm.title}
                        </a>
                      ) : (
                        <p className="font-serif text-base font-medium text-[#000000] leading-snug">{bm.title}</p>
                      )}
                      <span className="font-sans text-[10px] text-neutral-400 mt-1 block">{bm.saved_at ? new Date(bm.saved_at).toLocaleDateString() : ""}</span>
                    </div>
                    <button
                      onClick={() => {
                        toggleBookmark(bm);
                        setFullBookmarks(prev => prev.filter(b => b.url !== bm.url));
                      }}
                      title="Remove bookmark"
                      className="shrink-0 p-1.5 text-neutral-300 hover:text-black transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* REPORT ARTICLE MODAL */}
      {showReportModal && reportTarget && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowReportModal(false)}
          data-testid="report-modal"
        >
          <div
            className="w-full max-w-sm bg-white border border-black/10 p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-neutral-400 font-bold block mb-0.5">
                  Content Moderation
                </span>
                <h3 className="font-serif text-2xl font-medium text-[#000000]">Report Article</h3>
              </div>
              <button onClick={() => setShowReportModal(false)} className="p-1 text-neutral-400 hover:text-black transition-colors focus:outline-none">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="font-sans text-xs text-neutral-500 mb-5 line-clamp-2 italic">&ldquo;{reportTarget.title}&rdquo;</p>
            <div className="space-y-2 mb-6">
              {[
                { value: "inappropriate", label: "Inappropriate content" },
                { value: "inaccurate", label: "Inaccurate or misleading" },
                { value: "broken_link", label: "Broken link" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setReportReason(value)}
                  data-testid={`report-reason-${value}`}
                  className={`w-full text-left font-sans text-xs px-4 py-3 border transition-all duration-150 ${reportReason === value ? "bg-black text-white border-black" : "border-[#E0E0E0] text-neutral-600 hover:border-black hover:text-black"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={submitReport}
              disabled={!reportReason}
              data-testid="report-submit-btn"
              className="w-full font-sans text-xs uppercase tracking-[0.15em] font-semibold bg-black text-white py-3.5 hover:bg-neutral-800 transition-all disabled:opacity-40 disabled:cursor-not-allowed rounded-none focus:outline-none"
            >
              Submit Report
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  // Paths accessible without authentication (email links, legal pages)
  const PUBLIC_PATHS = ["/verify-email", "/reset-password", "/forgot-password", "/terms", "/disclaimer", "/privacy"];
  const isPublicPath = PUBLIC_PATHS.some(p => window.location.pathname === p);

  const [authUser, setAuthUser] = useState(null);
  // Skip the auth loading spinner for public paths — they render immediately
  const [authLoading, setAuthLoading] = useState(!isPublicPath);
  const [showTour, setShowTour] = useState(false);
  const handleTourDone = () => {
    localStorage.setItem("arvay_tour_done", "1");
    setShowTour(false);
  };
  // ageAccepted: flipped to true by handleAgeAccept to force a re-render after
  // writing the localStorage key. The gate visibility itself is computed from
  // authUser + localStorage at render time, so it can never be out-of-sync.
  const [ageAccepted, setAgeAccepted] = useState(false);
  const [ageDeclined, setAgeDeclined] = useState(false);

  // Ref set to true only when user clicks "I Accept".
  // handleAgeGateUnmount reads this ref — it fires when AgeGate leaves the DOM.
  // This mirrors "dynamically inject WalkMe script only after the gate unmounts."
  const tourTriggerRef = useRef(false);

  // Called by AgeGate's useEffect cleanup — i.e. the exact moment AgeGate unmounts.
  // This is the ONLY place the tour timer is scheduled. Never on page load.
  const handleAgeGateUnmount = () => {
    if (tourTriggerRef.current && !localStorage.getItem("arvay_tour_done")) {
      tourTriggerRef.current = false; // one-shot
      setTimeout(() => setShowTour(true), 500);
    }
  };

  useEffect(() => {
    if (isPublicPath) return; // no auth check needed for public paths
    const checkAuth = async () => {
      // ── Fresh email-verification redirect ──────────────────────────────────
      // When VerifyEmailPage redirects to /?verified=true, a stale arvay_token
      // in localStorage (from a PREVIOUS user on this device) could auto-login
      // that user, whose age gate was already accepted, causing the brand-new
      // account to bypass the gate entirely.
      // Fix: clear every session trace so the user MUST log in manually.
      const params = new URLSearchParams(window.location.search);
      if (params.get("verified") === "true") {
        try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
        localStorage.removeItem("arvay_token");
        setAuthUser(false);
        setAuthLoading(false);
        return;
      }
      // ── Normal session restore ─────────────────────────────────────────────
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAuthUser(res.data);
        // Gate visibility is computed at render time from localStorage —
        // no setShowAgeGate call needed here.

      } catch {
        setAuthUser(false);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);

  const handleAuth = (user) => {
    // ── Stale-key cleanup ─────────────────────────────────────────────────────
    // Remove age-verification keys that belong to a DIFFERENT user previously
    // logged in on this device. This ensures a new account on a shared device
    // is never silently treated as already-verified.
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith("arvay_age_verified_") && key !== `arvay_age_verified_${user.id}`) {
        localStorage.removeItem(key);
      }
    });
    // Explicit reset — belt-and-suspenders against any carry-over state.
    setAgeAccepted(false);
    setAuthUser(user);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem("arvay_token");
    setShowTour(false);
    setAgeAccepted(false);
    setAuthUser(false);
  };

  const handleAgeAccept = () => {
    localStorage.setItem(`arvay_age_verified_${authUser.id}`, "1");
    // Signal the unmount handler to inject the tour once AgeGate leaves the DOM.
    tourTriggerRef.current = true;
    setAgeAccepted(true);
  };

  const handleAgeDecline = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    // Do not write anything — clear any existing acceptance flag for this user
    localStorage.removeItem("arvay_token");
    if (authUser?.id) localStorage.removeItem(`arvay_age_verified_${authUser.id}`);
    setAgeAccepted(false);
    setAgeDeclined(true);
    setAuthUser(false);
  };

  // ── Public routes (email links, legal) — no auth required ──
  if (isPublicPath) {
    return (
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/verify-email"    element={<VerifyEmailPage />} />
          <Route path="/reset-password"  element={<ResetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/terms"           element={<TermsOfUsePage />} />
          <Route path="/disclaimer"      element={<DisclaimerPage />} />
          <Route path="/privacy"         element={<PrivacyPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Loading spinner while checking session
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="text-center">
          <p
            className="text-3xl tracking-[0.25em] uppercase font-light text-[#1A1A1A] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Arvay
          </p>
          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Not authenticated — show login/register gate
  if (!authUser) {
    return (
      <>
        <Toaster position="top-right" richColors />
        <LoginPage onAuth={handleAuth} ageDeclined={ageDeclined} />
      </>
    );
  }

  // Age gate — computed synchronously from authUser + localStorage.
  // No state dependency, so it cannot be skipped by a batching race.
  const needsAgeGate = !!authUser && !ageAccepted &&
    !localStorage.getItem(`arvay_age_verified_${authUser?.id}`);

  if (needsAgeGate) {
    return (
      <>
        <Toaster position="top-right" richColors />
        <AgeGate onAccept={handleAgeAccept} onDecline={handleAgeDecline} onAfterUnmount={handleAgeGateUnmount} />
      </>
    );
  }

  return (
    <BrowserRouter>
      {/* OnboardingTour injected here at App level — completely outside DashboardPage.
          It only mounts after AgeGate's onAfterUnmount fires (handleAgeGateUnmount).
          This guarantees zero overlap with the AgeGate under any timing condition.
          Never rendered on /admin. */}
      {showTour && window.location.pathname !== "/admin" && <OnboardingTour onDone={handleTourDone} />}
      <Routes>
        <Route path="/" element={<DashboardPage authUser={authUser} onLogout={handleLogout} onTourStart={() => setShowTour(true)} />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/privacy"    element={<PrivacyPage />} />
        <Route
          path="/admin"
          element={
            !authUser
              ? <DashboardPage authUser={authUser} onLogout={handleLogout} onTourStart={() => setShowTour(true)} />
              : authUser.email === "support@arvay.live"
                ? <AdminPanel authUser={authUser} onBack={() => { window.location.href = "/"; }} onLogout={handleLogout} />
                : (
                  <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                    <div className="text-center px-6">
                      <p className="text-2xl font-light tracking-wider text-[#1A1A1A] mb-4">You are not authorised to access this page.</p>
                      <button onClick={() => { window.location.href = "/"; }} className="font-sans text-[10px] uppercase tracking-[0.18em] font-semibold border border-black px-5 py-2 hover:bg-black hover:text-white transition-colors">
                        Go to Dashboard
                      </button>
                    </div>
                  </div>
                )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
